'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { usePublicChannels } from '../hooks/usePublicChannels';
import { usePersonalRooms } from '../hooks/usePersonalRooms';
import { useDMMessages, DMConversation, DMMessage } from '../hooks/useDMMessages';
import { useFriendCall, CallState, IncomingCall, ActiveCall } from '../hooks/useFriendCall';
import { useProfile, Profile } from '../hooks/useProfile';
import { useNotifications, Notification } from '../hooks/useNotifications';
import { useFriendRequests } from '../hooks/useFriendRequests';
import { supabase } from '../lib/supabase';

type UiStyle = 'standard' | 'jarvis';

// ============== ISLAND SYSTEM ==============

// All available island types
type IslandType = 'navigation' | 'chat' | 'controls' | 'friends' | 'settings' | 'rooms' | 'music' | 'notifications' | 'media';

interface IslandConfig {
  id: IslandType;
  name: string;
  icon: string;
  description: string;
  minWidth: number;
  minHeight: number;
  defaultWidth: number;
  defaultHeight: number;
  color: string; // For the planet modal
}

// Core/official GJYL islands
const OFFICIAL_ISLANDS: IslandType[] = ['navigation', 'chat', 'controls'];

// Custom icon renderer for glassmorphism style
const renderIslandIcon = (id: IslandType, size: 'sm' | 'md' | 'lg' = 'md') => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };
  const base = sizeClasses[size];

  switch (id) {
    case 'navigation':
      return (
        <div className={`${base} flex items-center justify-center`}>
          <div className="relative w-full h-full">
            <div className="absolute inset-1 border-2 border-current rounded-full opacity-60" />
            <div className="absolute top-1/2 left-1/2 w-0.5 h-1/2 -translate-x-1/2 -translate-y-1/4 bg-current rounded-full origin-bottom rotate-45" />
          </div>
        </div>
      );
    case 'chat':
      return (
        <div className={`${base} flex items-center justify-center`}>
          <div className="w-3/4 h-2/3 border-2 border-current rounded-lg relative">
            <div className="absolute -bottom-1 left-1 w-2 h-2 border-l-2 border-b-2 border-current transform -rotate-45 bg-slate-900/80" />
          </div>
        </div>
      );
    case 'controls':
      return (
        <div className={`${base} flex items-center justify-center`}>
          <div className="relative w-full h-full">
            <div className="absolute inset-1 border-2 border-current rounded-full" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1 h-2 bg-current rounded-full" />
            <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1.5 bg-current rounded-full" />
          </div>
        </div>
      );
    case 'friends':
      return (
        <div className={`${base} flex items-center justify-center`}>
          <div className="flex -space-x-1">
            <div className="w-3 h-3 rounded-full border-2 border-current" />
            <div className="w-3 h-3 rounded-full border-2 border-current opacity-60" />
          </div>
        </div>
      );
    case 'settings':
      return (
        <div className={`${base} flex flex-col items-center justify-center gap-0.5`}>
          <div className="flex items-center gap-0.5">
            <div className="w-1 h-1 rounded-full bg-current" />
            <div className="w-3 h-0.5 bg-current rounded-full" />
          </div>
          <div className="flex items-center gap-0.5">
            <div className="w-2 h-0.5 bg-current rounded-full" />
            <div className="w-1 h-1 rounded-full bg-current" />
          </div>
          <div className="flex items-center gap-0.5">
            <div className="w-1.5 h-0.5 bg-current rounded-full" />
            <div className="w-1 h-1 rounded-full bg-current opacity-60" />
          </div>
        </div>
      );
    case 'rooms':
      return (
        <div className={`${base} grid grid-cols-2 gap-0.5 p-0.5`}>
          <div className="rounded-sm bg-current opacity-80" />
          <div className="rounded-sm bg-current opacity-60" />
          <div className="rounded-sm bg-current opacity-40" />
          <div className="rounded-sm bg-current opacity-20" />
        </div>
      );
    case 'music':
      return (
        <div className={`${base} flex items-end justify-center gap-0.5 pb-0.5`}>
          <div className="w-1 h-2 rounded-full bg-current opacity-40" />
          <div className="w-1 h-4 rounded-full bg-current opacity-70" />
          <div className="w-1 h-3 rounded-full bg-current opacity-50" />
          <div className="w-1 h-5 rounded-full bg-current opacity-80" />
        </div>
      );
    case 'notifications':
      return (
        <div className={`${base} flex items-center justify-center`}>
          <div className="relative">
            <div className="w-3 h-3 rounded-full border-2 border-current" />
            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-current" />
          </div>
        </div>
      );
    case 'media':
      return (
        <div className={`${base} flex items-center justify-center`}>
          <div className="relative w-full h-full">
            {/* Play button triangle inside rounded rect */}
            <div className="absolute inset-1 rounded-md border-2 border-current" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5" />
            </div>
          </div>
        </div>
      );
    default:
      return <div className={`${base} rounded-full border-2 border-current`} />;
  }
};

// Island registry - all possible islands
const ISLAND_REGISTRY: Record<IslandType, IslandConfig> = {
  navigation: {
    id: 'navigation',
    name: 'Navigation',
    icon: 'nav',
    description: 'Channels & navigation',
    minWidth: 200,
    minHeight: 250,
    defaultWidth: 280,
    defaultHeight: 500,
    color: 'from-cyan-500 to-blue-600',
  },
  chat: {
    id: 'chat',
    name: 'Chat',
    icon: 'chat',
    description: 'Main chat area',
    minWidth: 350,
    minHeight: 300,
    defaultWidth: 600,
    defaultHeight: 540,
    color: 'from-purple-500 to-pink-600',
  },
  controls: {
    id: 'controls',
    name: 'Systems',
    icon: 'sys',
    description: 'Activity & controls',
    minWidth: 200,
    minHeight: 250,
    defaultWidth: 280,
    defaultHeight: 500,
    color: 'from-emerald-500 to-teal-600',
  },
  friends: {
    id: 'friends',
    name: 'Friends',
    icon: 'ppl',
    description: 'Friends list & status',
    minWidth: 180,
    minHeight: 200,
    defaultWidth: 250,
    defaultHeight: 400,
    color: 'from-amber-500 to-orange-600',
  },
  settings: {
    id: 'settings',
    name: 'Settings',
    icon: 'cfg',
    description: 'Quick settings panel',
    minWidth: 200,
    minHeight: 200,
    defaultWidth: 280,
    defaultHeight: 350,
    color: 'from-slate-500 to-zinc-600',
  },
  rooms: {
    id: 'rooms',
    name: 'Rooms',
    icon: 'rm',
    description: 'Browse & join rooms',
    minWidth: 200,
    minHeight: 200,
    defaultWidth: 300,
    defaultHeight: 400,
    color: 'from-indigo-500 to-violet-600',
  },
  music: {
    id: 'music',
    name: 'Music',
    icon: 'aud',
    description: 'Now playing & queue',
    minWidth: 200,
    minHeight: 150,
    defaultWidth: 300,
    defaultHeight: 200,
    color: 'from-rose-500 to-red-600',
  },
  notifications: {
    id: 'notifications',
    name: 'Alerts',
    icon: 'ntf',
    description: 'Notifications center',
    minWidth: 200,
    minHeight: 150,
    defaultWidth: 280,
    defaultHeight: 300,
    color: 'from-yellow-500 to-amber-600',
  },
  media: {
    id: 'media',
    name: 'Media',
    icon: 'vid',
    description: 'Watch together & media queue',
    minWidth: 300,
    minHeight: 250,
    defaultWidth: 480,
    defaultHeight: 360,
    color: 'from-fuchsia-500 to-pink-600',
  },
};

interface IslandPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DynamicIsland {
  type: IslandType;
  position: IslandPosition;
  visible: boolean;
  locked?: boolean; // Individual island lock - stays in place during snap
}

// Default active islands
const DEFAULT_ISLANDS: DynamicIsland[] = [
  { type: 'navigation', position: { x: 40, y: 80, width: 280, height: 500 }, visible: true },
  { type: 'chat', position: { x: 340, y: 60, width: 600, height: 540 }, visible: true },
  { type: 'controls', position: { x: 960, y: 80, width: 280, height: 500 }, visible: true },
];

const STORAGE_KEY = 'gjyl-jarvis-islands-v2';
const GAP = 20; // Gap between islands when snapping

// ============== GLASS PANEL STYLE ==============
// Bevel effect using inset shadows for 3D depth
const glassPanel =
  'relative overflow-hidden bg-gradient-to-b from-gray-800/30 to-black/80 backdrop-blur-xl border-t border-white/25 border-x border-white/8 border-b border-black/70 rounded-3xl';

// Bevel shadow for 3D raised edge effect
const bevelShadow = `
  inset 2px 2px 4px rgba(255,255,255,0.08),
  inset -2px -2px 4px rgba(0,0,0,0.4),
  0 0 40px rgba(34,211,238,0.25),
  0 24px 60px rgba(0,0,0,0.55),
  0 4px 16px rgba(0,0,0,0.4)
`;

// ============== COMPONENT PROPS ==============
interface FloatingHUDLayoutProps {
  children?: React.ReactNode;
  title?: string;
  className?: string;
  onSwitchStyle?: (style: UiStyle) => void;
  spaces?: any[];
  channels?: any[];
  members?: any[];
  activeSpaceId?: string;
  activeChannelId?: string;
  onSpaceSelect?: (spaceId: string) => void;
  onChannelSelect?: (channelId: string) => void;
  currentUser?: any;
}

const FOOTER_BARS = [25, 60, 35, 80, 45, 70, 30, 55, 65, 40, 75, 50];

// ============== DRAG HANDLE ==============
function DragHandle({ className = '' }: { className?: string }) {
  return (
    <div className={`cursor-move flex items-center justify-center gap-1 ${className}`}>
      <div className="flex flex-col gap-0.5 opacity-40 hover:opacity-70 transition-opacity">
        <div className="flex gap-0.5">
          <div className="w-1 h-1 rounded-full bg-cyan-400" />
          <div className="w-1 h-1 rounded-full bg-cyan-400" />
          <div className="w-1 h-1 rounded-full bg-cyan-400" />
        </div>
        <div className="flex gap-0.5">
          <div className="w-1 h-1 rounded-full bg-cyan-400" />
          <div className="w-1 h-1 rounded-full bg-cyan-400" />
          <div className="w-1 h-1 rounded-full bg-cyan-400" />
        </div>
      </div>
    </div>
  );
}

// ============== GLASS AVATAR ==============
// Cool glassmorphism placeholder avatar with abstract human silhouette
function GlassAvatar({
  src,
  name,
  size = 'md',
  status,
  onClick,
  className = '',
}: {
  src?: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  status?: 'online' | 'offline' | 'away' | 'dnd';
  onClick?: () => void;
  className?: string;
}) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-9 h-9',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  const statusColors = {
    online: 'bg-emerald-400',
    offline: 'bg-gray-500',
    away: 'bg-amber-400',
    dnd: 'bg-red-400',
  };

  const initial = name?.charAt(0).toUpperCase() || '?';

  const avatarContent = (
    <>
      {src ? (
        <img
          src={src}
          alt={name || 'Avatar'}
          className="w-full h-full rounded-full object-cover"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        // Glassmorphism placeholder with abstract human shape
        <div
          className="w-full h-full rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(139,92,246,0.15) 100%)',
            boxShadow: 'inset 0 0 20px rgba(34,211,238,0.1), 0 4px 20px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {/* Abstract human silhouette using CSS */}
          <div className="relative" style={{ transform: size === 'sm' ? 'scale(0.6)' : size === 'lg' ? 'scale(1.3)' : size === 'xl' ? 'scale(1.6)' : 'scale(1)' }}>
            {/* Head */}
            <div
              className="w-3 h-3 rounded-full mx-auto"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                boxShadow: 'inset 0 -1px 3px rgba(0,0,0,0.2)',
              }}
            />
            {/* Body/Shoulders */}
            <div
              className="w-6 h-3 rounded-t-full mt-0.5"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
                boxShadow: 'inset 0 -1px 3px rgba(0,0,0,0.15)',
              }}
            />
          </div>
        </div>
      )}

      {/* Status indicator */}
      {status && (
        <div
          className={`absolute bottom-0 right-0 rounded-full border-2 border-slate-900 ${statusColors[status]}`}
          style={{
            width: size === 'sm' ? '8px' : size === 'md' ? '10px' : size === 'lg' ? '12px' : '14px',
            height: size === 'sm' ? '8px' : size === 'md' ? '10px' : size === 'lg' ? '12px' : '14px',
          }}
        />
      )}
    </>
  );

  // Only render as button if onClick is provided (avoids nested button issues)
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={`relative rounded-full flex-shrink-0 ${sizeClasses[size]} ${className} cursor-pointer hover:ring-2 hover:ring-cyan-400/50 transition-all`}
      >
        {avatarContent}
      </button>
    );
  }

  return (
    <div
      className={`relative rounded-full flex-shrink-0 ${sizeClasses[size]} ${className} cursor-default transition-all`}
    >
      {avatarContent}
    </div>
  );
}

// ============== USER CONTEXT MENU ==============
interface ContextMenuOption {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  separator?: boolean;
}

interface UserContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  userId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string | null;
  isFriend?: boolean;
  isPendingSent?: boolean;
  isPendingReceived?: boolean;
  isBlocked?: boolean;
  isCurrentUser?: boolean;
  onViewProfile?: () => void;
  onSendMessage?: () => void;
  onVoiceCall?: () => void;
  onVideoCall?: () => void;
  onAddFriend?: () => void;
  onRemoveFriend?: () => void;
  onAcceptFriend?: () => void;
  onBlockUser?: () => void;
  onUnblockUser?: () => void;
  onCopyUserId?: () => void;
}

function UserContextMenu({
  isOpen,
  position,
  onClose,
  userId,
  username,
  displayName,
  avatarUrl,
  isFriend = false,
  isPendingSent = false,
  isPendingReceived = false,
  isBlocked = false,
  isCurrentUser = false,
  onViewProfile,
  onSendMessage,
  onVoiceCall,
  onVideoCall,
  onAddFriend,
  onRemoveFriend,
  onAcceptFriend,
  onBlockUser,
  onUnblockUser,
  onCopyUserId,
}: UserContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Build options based on relationship
  const options: ContextMenuOption[] = [];

  // View Profile (always available)
  if (onViewProfile) {
    options.push({
      label: 'View Profile',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      onClick: () => { onViewProfile(); onClose(); },
    });
  }

  // Message & Call options (only if friend)
  if (isFriend && !isCurrentUser) {
    if (onSendMessage) {
      options.push({
        label: 'Send Message',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        ),
        onClick: () => { onSendMessage(); onClose(); },
      });
    }

    if (onVoiceCall) {
      options.push({
        label: 'Voice Call',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        ),
        onClick: () => { onVoiceCall(); onClose(); },
      });
    }

    if (onVideoCall) {
      options.push({
        label: 'Video Call',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        ),
        onClick: () => { onVideoCall(); onClose(); },
      });
    }
  }

  // Separator before friend actions
  if (options.length > 0 && !isCurrentUser) {
    options.push({ label: '', icon: null, onClick: () => {}, separator: true });
  }

  // Friend management options
  if (!isCurrentUser) {
    if (isPendingReceived && onAcceptFriend) {
      options.push({
        label: 'Accept Friend Request',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        ),
        onClick: () => { onAcceptFriend(); onClose(); },
      });
    } else if (!isFriend && !isPendingSent && onAddFriend) {
      options.push({
        label: 'Add Friend',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        ),
        onClick: () => { onAddFriend(); onClose(); },
      });
    } else if (isPendingSent) {
      options.push({
        label: 'Friend Request Pending',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => {},
      });
    }

    if (isFriend && onRemoveFriend) {
      options.push({
        label: 'Remove Friend',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
          </svg>
        ),
        onClick: () => { onRemoveFriend(); onClose(); },
        danger: true,
      });
    }
  }

  // Separator before dangerous actions
  if (!isCurrentUser && (onBlockUser || onUnblockUser)) {
    options.push({ label: '', icon: null, onClick: () => {}, separator: true });
  }

  // Block/Unblock
  if (!isCurrentUser) {
    if (isBlocked && onUnblockUser) {
      options.push({
        label: 'Unblock User',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => { onUnblockUser(); onClose(); },
      });
    } else if (!isBlocked && onBlockUser) {
      options.push({
        label: 'Block User',
        icon: (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        ),
        onClick: () => { onBlockUser(); onClose(); },
        danger: true,
      });
    }
  }

  // Copy User ID (always available)
  if (onCopyUserId) {
    options.push({ label: '', icon: null, onClick: () => {}, separator: true });
    options.push({
      label: 'Copy User ID',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
        </svg>
      ),
      onClick: () => {
        navigator.clipboard.writeText(userId);
        onCopyUserId();
        onClose();
      },
    });
  }

  // Adjust position to stay within viewport
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 200),
    y: Math.min(position.y, window.innerHeight - (options.length * 36 + 60)),
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999] min-w-[180px] max-w-[220px] bg-gray-900/98 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-scale-in"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      {/* User header */}
      <div className="px-3 py-2 bg-white/5 border-b border-white/10 flex items-center gap-2">
        <GlassAvatar
          src={avatarUrl}
          name={displayName || username}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">{displayName || username}</div>
          <div className="text-[10px] text-slate-400 truncate">@{username}</div>
        </div>
      </div>

      {/* Options */}
      <div className="py-1">
        {options.map((option, index) => {
          if (option.separator) {
            return <div key={index} className="my-1 border-t border-white/10" />;
          }
          return (
            <button
              key={index}
              onClick={option.onClick}
              className={`w-full px-3 py-2 flex items-center gap-2 text-left transition-colors ${
                option.danger
                  ? 'text-red-400 hover:bg-red-500/20'
                  : 'text-slate-200 hover:bg-white/10'
              }`}
            >
              <span className={option.danger ? 'text-red-400' : 'text-slate-400'}>{option.icon}</span>
              <span className="text-sm">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============== PROFILE MODAL ==============
function ProfileModal({
  isOpen,
  onClose,
  profile,
  isOwnProfile = false,
  onUpdateProfile,
  onUploadAvatar,
}: {
  isOpen: boolean;
  onClose: () => void;
  profile: Profile | null;
  isOwnProfile?: boolean;
  onUpdateProfile?: (updates: Partial<Profile>) => Promise<boolean>;
  onUploadAvatar?: (file: File) => Promise<string | null>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedBio, setEditedBio] = useState('');
  const [editedStatus, setEditedStatus] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setEditedBio(profile.bio || '');
      setEditedStatus(profile.custom_status || '');
    }
  }, [profile]);

  if (!isOpen || !profile) return null;

  const handleSave = async () => {
    if (onUpdateProfile) {
      await onUpdateProfile({
        bio: editedBio,
        custom_status: editedStatus,
      });
      setIsEditing(false);
    }
  };

  const handleAvatarClick = () => {
    if (isOwnProfile && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUploadAvatar) {
      await onUploadAvatar(file);
    }
  };

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-sm rounded-3xl p-6 overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.98) 100%)',
          boxShadow: 'inset 1px 1px 2px rgba(255,255,255,0.1), 0 25px 50px rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Avatar section */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative group">
            <GlassAvatar
              src={profile.avatar_url}
              name={profile.display_name}
              size="xl"
              status={profile.status}
              onClick={isOwnProfile ? handleAvatarClick : undefined}
              className={isOwnProfile ? 'cursor-pointer' : ''}
            />
            {isOwnProfile && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={handleAvatarClick}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <h2 className="text-lg font-semibold text-white mt-4">{profile.display_name}</h2>
          {profile.username && (
            <p className="text-sm text-slate-400">@{profile.username}</p>
          )}
          {profile.pronouns && (
            <p className="text-xs text-slate-500 mt-1">{profile.pronouns}</p>
          )}
        </div>

        {/* Custom status */}
        {(profile.custom_status || (isOwnProfile && isEditing)) && (
          <div className="mb-4">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block mb-1">Status</label>
            {isEditing ? (
              <input
                type="text"
                value={editedStatus}
                onChange={(e) => setEditedStatus(e.target.value)}
                placeholder="What's on your mind?"
                className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50"
              />
            ) : (
              <p className="text-sm text-slate-300 italic">{profile.custom_status || 'No status set'}</p>
            )}
          </div>
        )}

        {/* Bio */}
        <div className="mb-4">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 block mb-1">About</label>
          {isEditing ? (
            <textarea
              value={editedBio}
              onChange={(e) => setEditedBio(e.target.value)}
              placeholder="Tell us about yourself..."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 resize-none"
            />
          ) : (
            <p className="text-sm text-slate-300">{profile.bio || 'No bio yet'}</p>
          )}
        </div>

        {/* Member since */}
        <div className="text-xs text-slate-500 text-center mb-4">
          Member since {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>

        {/* Edit/Save buttons */}
        {isOwnProfile && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-sm hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-200 text-sm hover:bg-cyan-500/30 transition-colors"
                >
                  Save
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="w-full py-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-sm hover:bg-white/10 transition-colors"
              >
                Edit Profile
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============== ADD FRIEND MODAL ==============
function AddFriendModal({
  isOpen,
  onClose,
  onSendRequest,
  loading,
  currentUserId,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSendRequest: (userId: string, username: string) => Promise<{ success: boolean; error?: string }>;
  loading: boolean;
  currentUserId?: string;
}) {
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    friendship_status?: 'none' | 'friends' | 'pending_sent' | 'pending_received';
  }>>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Discord-style instant search with debounce
  useEffect(() => {
    if (!searchInput.trim() || !currentUserId) {
      setSearchResults([]);
      setError(null);
      return;
    }

    // Debounce - wait 150ms after typing stops (Discord-like instant feel)
    const timeoutId = setTimeout(() => {
      performSearch(searchInput.trim());
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [searchInput, currentUserId]);

  // Perform the actual search
  const performSearch = async (query: string) => {
    if (!query || !currentUserId) return;

    setSearching(true);
    setError(null);

    try {
      // Search for users by username (case-insensitive partial match)
      const { data: users, error: searchError } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .ilike('username', `%${query}%`)
        .neq('id', currentUserId)
        .limit(8);

      if (searchError) {
        console.error('Search error:', searchError);
        setError('Search failed');
        return;
      }

      if (!users || users.length === 0) {
        setSearchResults([]);
        setError('No users found');
        return;
      }

      // Get friendship status for each user
      const userIds = users.map(u => u.id);
      const { data: friendships } = await supabase
        .from('friendships')
        .select('*')
        .or(`user_id.eq.${currentUserId},friend_id.eq.${currentUserId}`)
        .in('friend_id', [...userIds, currentUserId])
        .in('user_id', [...userIds, currentUserId]);

      // Enrich users with friendship status
      const enrichedUsers = users.map(user => {
        const outgoing = friendships?.find(f => f.user_id === currentUserId && f.friend_id === user.id);
        const incoming = friendships?.find(f => f.user_id === user.id && f.friend_id === currentUserId);

        let friendship_status: 'none' | 'friends' | 'pending_sent' | 'pending_received' = 'none';
        if (outgoing?.status === 'accepted' || incoming?.status === 'accepted') {
          friendship_status = 'friends';
        } else if (outgoing?.status === 'pending') {
          friendship_status = 'pending_sent';
        } else if (incoming?.status === 'pending') {
          friendship_status = 'pending_received';
        }

        return { ...user, friendship_status };
      });

      setSearchResults(enrichedUsers);
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Legacy search handler (for Enter key)
  const handleSearch = () => {
    if (searchInput.trim()) {
      performSearch(searchInput.trim());
    }
  };

  // Send friend request
  const handleSendRequest = async (userId: string, username: string) => {
    setError(null);
    setSuccess(null);

    const result = await onSendRequest(userId, username);

    if (result.success) {
      setSuccess(`Friend request sent to @${username}!`);
      // Remove from search results
      setSearchResults(prev => prev.filter(u => u.id !== userId));
      // Clear success after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(result.error || 'Failed to send request');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-3xl flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900/95 border border-white/10 rounded-2xl p-4 w-full max-w-xs">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white">Add Friend</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-xs text-gray-400 mb-3">Search for users by username</p>

        {/* Search Input */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Enter username..."
            className="flex-1 px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50"
            autoFocus
          />
          <button
            onClick={handleSearch}
            disabled={searching || !searchInput.trim()}
            className="px-3 py-2 text-sm bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
          >
            {searching ? '...' : 'Search'}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-3 p-2 rounded-lg bg-red-500/10 border border-red-400/30 text-red-300 text-xs">
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-3 p-2 rounded-lg bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-xs">
            {success}
          </div>
        )}

        {/* Loading indicator */}
        {searching && (
          <div className="flex items-center justify-center py-4">
            <div className="w-5 h-5 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
          </div>
        )}

        {/* Search Results */}
        {!searching && searchResults.length > 0 && (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {searchResults.map((user) => (
              <div
                key={user.id}
                className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              >
                <GlassAvatar
                  src={user.avatar_url}
                  name={user.display_name || user.username}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-white block truncate">
                    {user.display_name || user.username}
                  </span>
                  <span className="text-[10px] text-slate-400 block truncate">
                    @{user.username}
                  </span>
                </div>
                {/* Friendship status badge/action */}
                {user.friendship_status === 'friends' && (
                  <span className="px-2 py-1 text-[10px] bg-emerald-500/20 border border-emerald-400/30 rounded-lg text-emerald-300">
                    Friends
                  </span>
                )}
                {user.friendship_status === 'pending_sent' && (
                  <span className="px-2 py-1 text-[10px] bg-yellow-500/20 border border-yellow-400/30 rounded-lg text-yellow-300">
                    Pending
                  </span>
                )}
                {user.friendship_status === 'pending_received' && (
                  <span className="px-2 py-1 text-[10px] bg-blue-500/20 border border-blue-400/30 rounded-lg text-blue-300">
                    Accept?
                  </span>
                )}
                {(!user.friendship_status || user.friendship_status === 'none') && (
                  <button
                    onClick={() => handleSendRequest(user.id, user.username)}
                    disabled={loading}
                    className="px-2 py-1 text-[10px] bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/30 rounded-lg text-cyan-300 transition-colors disabled:opacity-50"
                  >
                    {loading ? '...' : 'Add'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty State - typing hint */}
        {!searching && searchResults.length === 0 && !error && !searchInput.trim() && (
          <div className="text-center text-slate-500 py-4">
            <p className="text-xs">Start typing to search...</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============== 3D PLANET MODAL ==============
// CSS keyframes injected once
const carouselStyles = `
@keyframes spin-carousel {
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
}
@keyframes float-gentle {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
}
@keyframes pulse-hand {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
}
`;

function PlanetModal({
  isOpen,
  onClose,
  islands,
  onToggleIsland,
  onSnapIslands,
}: {
  isOpen: boolean;
  onClose: () => void;
  islands: DynamicIsland[];
  onToggleIsland: (type: IslandType) => void;
  onSnapIslands: () => void;
}) {
  const [isPaused, setIsPaused] = useState(false);

  // Manual drag state
  const [isDragging, setIsDragging] = useState(false);
  const [manualRotation, setManualRotation] = useState(0);
  const [useManualRotation, setUseManualRotation] = useState(false);
  const [velocity, setVelocity] = useState(0);
  const dragStartRef = React.useRef<{ x: number; rotation: number } | null>(null);
  const lastMoveRef = React.useRef<{ x: number; time: number }[]>([]);
  const animationFrameRef = React.useRef<number | null>(null);

  // Momentum decay animation
  useEffect(() => {
    if (!useManualRotation || isDragging || Math.abs(velocity) < 0.1) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // If velocity is very low, switch back to auto-rotation
      if (useManualRotation && Math.abs(velocity) < 0.1 && !isDragging) {
        setTimeout(() => setUseManualRotation(false), 500);
      }
      return;
    }

    const animate = () => {
      setManualRotation(prev => prev + velocity);
      setVelocity(prev => prev * 0.96); // Decay factor
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [velocity, useManualRotation, isDragging]);

  const handleDragStart = (e: React.MouseEvent) => {
    setIsDragging(true);
    setUseManualRotation(true);
    setVelocity(0);
    dragStartRef.current = { x: e.clientX, rotation: manualRotation };
    lastMoveRef.current = [{ x: e.clientX, time: Date.now() }];
  };

  const handleDragMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStartRef.current) return;

    const delta = e.clientX - dragStartRef.current.x;
    const newRotation = dragStartRef.current.rotation + (delta * 0.3);
    setManualRotation(newRotation);

    // Track positions for velocity calculation
    lastMoveRef.current.push({ x: e.clientX, time: Date.now() });
    if (lastMoveRef.current.length > 5) lastMoveRef.current.shift();
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    // Calculate velocity from recent movements
    const moves = lastMoveRef.current;
    if (moves.length >= 2) {
      const recent = moves[moves.length - 1];
      const earlier = moves[0];
      const timeDelta = recent.time - earlier.time;
      if (timeDelta > 0) {
        const pixelDelta = recent.x - earlier.x;
        const calculatedVelocity = (pixelDelta / timeDelta) * 10; // Scale factor
        setVelocity(calculatedVelocity);
      }
    }
  };

  if (!isOpen) return null;

  const activeTypes = islands.filter(i => i.visible).map(i => i.type);
  const islandList = Object.values(ISLAND_REGISTRY);
  const sphereRadius = 170;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Inject CSS animations */}
      <style>{carouselStyles}</style>

      {/* Clean backdrop */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-slate-950 via-black to-slate-950"
        onClick={onClose}
      />

      {/* Modal content */}
      <div className="relative z-10 flex flex-col items-center gap-6">
        {/* Title */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-purple-400">
            Island Control
          </h2>
          <p className="text-slate-500 text-sm mt-1">Click to toggle • Drag to spin</p>
        </div>

        {/* 3D Sphere Container */}
        <div
          className="relative w-[420px] h-[420px]"
          style={{
            perspective: '1000px',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onMouseEnter={() => !isDragging && setIsPaused(true)}
          onMouseLeave={() => { setIsPaused(false); handleDragEnd(); }}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
        >
          {/* 3D Scene - tilted for depth */}
          <div
            className="absolute inset-0"
            style={{
              transformStyle: 'preserve-3d',
              transform: 'rotateX(15deg)',
            }}
          >
            {/* Planet Core */}
            <div
              className="absolute left-1/2 top-1/2 w-[160px] h-[160px] -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                background: 'radial-gradient(circle at 35% 35%, #1a365d, #0f172a 70%, #020617)',
                boxShadow: `
                  inset -20px -20px 40px rgba(0,0,0,0.8),
                  inset 15px 15px 30px rgba(34,211,238,0.08),
                  0 0 60px rgba(34,211,238,0.2)
                `,
              }}
            >
              {/* Center count */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-100">{activeTypes.length}</div>
                  <div className="text-[9px] text-cyan-400/60 uppercase tracking-widest">Active</div>
                </div>
              </div>
            </div>

            {/* Orbiting Islands - CSS Animation or Manual */}
            <div
              className="absolute left-1/2 top-1/2"
              style={{
                transformStyle: 'preserve-3d',
                ...(useManualRotation
                  ? { transform: `rotateY(${manualRotation}deg)` }
                  : {
                      animation: 'spin-carousel 24s linear infinite',
                      animationPlayState: isPaused ? 'paused' : 'running',
                    }
                ),
                willChange: 'transform',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
              }}
            >
              {islandList.map((config, index) => {
                const isActive = activeTypes.includes(config.id);
                const theta = (index / islandList.length) * Math.PI * 2;
                const x = Math.sin(theta) * sphereRadius;
                const z = Math.cos(theta) * sphereRadius;
                const rotateY = theta * 180 / Math.PI;

                return (
                  <button
                    key={config.id}
                    onClick={(e) => { e.stopPropagation(); if (!isDragging) onToggleIsland(config.id); }}
                    className={`absolute w-16 h-16 -ml-8 -mt-8 rounded-xl flex flex-col items-center justify-center gap-1
                      ${isActive
                        ? `bg-gradient-to-br ${config.color} text-white`
                        : 'bg-slate-800/90 border border-slate-700 hover:border-slate-500 text-slate-300'
                      }`}
                    style={{
                      transform: `translateX(${x}px) translateZ(${z}px) rotateY(${-rotateY}deg)`,
                      boxShadow: isActive ? '0 0 25px rgba(255,255,255,0.2)' : 'none',
                      willChange: 'transform',
                    }}
                  >
                    {renderIslandIcon(config.id, 'lg')}
                    <span className="text-[8px] font-mono uppercase opacity-80">{config.name}</span>
                    {isActive && (
                      <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-emerald-400/90 flex items-center justify-center">
                        <div className="w-2 h-2 rotate-45 bg-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Orbital ring */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none rounded-full border border-cyan-500/10"
              style={{
                width: sphereRadius * 2 + 20,
                height: sphereRadius * 2 + 20,
                transform: 'translateX(-50%) translateY(-50%) rotateX(90deg)',
                transformStyle: 'preserve-3d',
              }}
            />
          </div>

          {/* Hand icon - grab indicator */}
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none"
            style={{ animation: isDragging ? 'none' : 'pulse-hand 2s ease-in-out infinite' }}
          >
            {/* Custom hand icon - glassmorphism style */}
            <div className={`w-10 h-10 rounded-xl bg-white/5 border border-white/20 flex items-center justify-center transition-all ${isDragging ? 'bg-cyan-500/20 border-cyan-400/40 scale-110' : ''}`}>
              {/* Palm */}
              <div className="relative">
                <div className="w-4 h-5 rounded-t-full rounded-b-lg bg-cyan-400/40 relative">
                  {/* Fingers */}
                  <div className="absolute -top-2 left-0 w-1 h-2.5 rounded-full bg-cyan-400/50" />
                  <div className="absolute -top-3 left-1 w-1 h-3 rounded-full bg-cyan-400/60" />
                  <div className="absolute -top-3 left-2 w-1 h-3 rounded-full bg-cyan-400/50" />
                  <div className="absolute -top-2 left-3 w-1 h-2 rounded-full bg-cyan-400/40" />
                  {/* Thumb */}
                  <div className="absolute top-0.5 -left-1.5 w-1.5 h-2 rounded-full bg-cyan-400/40 rotate-[-30deg]" />
                </div>
              </div>
            </div>
            <span className="text-[9px] text-cyan-400/50 font-mono uppercase">{isDragging ? 'Spinning...' : 'Drag'}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={onSnapIslands}
            className="px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
          >
            {/* Custom snap icon */}
            <div className="w-4 h-4 relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-2 bg-current rounded-full" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0.5 h-2 bg-current rounded-full" />
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-0.5 bg-current rounded-full" />
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-0.5 bg-current rounded-full" />
            </div>
            Snap Together
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-full bg-white/10 text-white text-sm border border-white/20 hover:bg-white/20 transition-colors"
          >
            Done
          </button>
        </div>

        {/* Quick toggle legend */}
        <div className="flex flex-wrap justify-center gap-2 max-w-md">
          {islandList.map((config) => {
            const isActive = activeTypes.includes(config.id);
            return (
              <button
                key={config.id}
                onClick={() => onToggleIsland(config.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors
                  ${isActive
                    ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-500/30'
                    : 'text-slate-500 hover:text-slate-300 border border-transparent'
                  }`}
              >
                {renderIslandIcon(config.id, 'sm')}
                <span>{config.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============== MAIN COMPONENT ==============
export default function FloatingHUDLayout({
  children,
  title = 'GJYL PUBLIC HUB',
  className = '',
  onSwitchStyle,
  members = [],
  onChannelSelect,
  currentUser,
}: FloatingHUDLayoutProps) {
  // Dynamic islands state
  const [islands, setIslands] = useState<DynamicIsland[]>(DEFAULT_ISLANDS);
  const [activeIsland, setActiveIsland] = useState<string | null>(null);
  const [isLayoutLocked, setIsLayoutLocked] = useState(false);
  const [showPlanetModal, setShowPlanetModal] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);

  // UI state
  const [bgIntensity, setBgIntensity] = useState(0.85);
  const [panelOpacity, setPanelOpacity] = useState(0.78);
  const [backgroundMotion, setBackgroundMotion] = useState(false);

  // Effects and visual settings
  const [glowEffects, setGlowEffects] = useState(() => {
    const saved = localStorage.getItem('gjyl-glow-effects');
    return saved === null ? true : saved === 'true';
  });
  const [animationsEnabled, setAnimationsEnabled] = useState(() => {
    const saved = localStorage.getItem('gjyl-animations');
    return saved === null ? true : saved === 'true';
  });
  const [blurEffects, setBlurEffects] = useState(() => {
    const saved = localStorage.getItem('gjyl-blur-effects');
    return saved === null ? true : saved === 'true';
  });

  // Settings tab state
  const [settingsTab, setSettingsTab] = useState<'profile' | 'appearance' | 'audio' | 'layout'>('profile');

  // Profile editing state
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileUsername, setProfileUsername] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [profileStatus, setProfileStatus] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveSuccess, setProfileSaveSuccess] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Voice/Audio settings
  const [noiseSuppressionEnabled, setNoiseSuppressionEnabled] = useState(() => {
    const saved = localStorage.getItem('gjyl_noise_suppression');
    return saved === null ? true : saved === 'true';
  });
  const [inputVolume, setInputVolume] = useState(80);
  const [outputVolume, setOutputVolume] = useState(100);

  // Friends search and add
  const [friendSearchQuery, setFriendSearchQuery] = useState('');
  const [showAddFriendModal, setShowAddFriendModal] = useState(false);
  const [friendCodeInput, setFriendCodeInput] = useState('');

  // User context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    user: {
      id: string;
      username: string;
      display_name?: string | null;
      avatar_url?: string | null;
      isFriend?: boolean;
      isPendingSent?: boolean;
      isPendingReceived?: boolean;
    } | null;
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
    user: null,
  });

  // Handle right-click on a user
  const handleUserContextMenu = useCallback((
    e: React.MouseEvent,
    user: {
      id: string;
      username: string;
      display_name?: string | null;
      avatar_url?: string | null;
      isFriend?: boolean;
      isPendingSent?: boolean;
      isPendingReceived?: boolean;
    }
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      isOpen: true,
      position: { x: e.clientX, y: e.clientY },
      user,
    });
  }, []);

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Save effect preferences to localStorage
  useEffect(() => {
    localStorage.setItem('gjyl-glow-effects', String(glowEffects));
    localStorage.setItem('gjyl-animations', String(animationsEnabled));
    localStorage.setItem('gjyl-blur-effects', String(blurEffects));
    localStorage.setItem('gjyl_noise_suppression', String(noiseSuppressionEnabled));
  }, [glowEffects, animationsEnabled, blurEffects, noiseSuppressionEnabled]);

  // Dynamic styles based on effect settings
  const dynamicGlassPanel = blurEffects
    ? 'relative overflow-hidden bg-gradient-to-b from-gray-800/30 to-black/80 backdrop-blur-xl border-t border-white/25 border-x border-white/8 border-b border-black/70 rounded-3xl'
    : 'relative overflow-hidden bg-gradient-to-b from-gray-800/50 to-black/90 border-t border-white/25 border-x border-white/8 border-b border-black/70 rounded-3xl';

  const dynamicBevelShadow = glowEffects
    ? `inset 2px 2px 4px rgba(255,255,255,0.08), inset -2px -2px 4px rgba(0,0,0,0.4), 0 0 40px rgba(34,211,238,0.25), 0 24px 60px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.4)`
    : `inset 2px 2px 4px rgba(255,255,255,0.08), inset -2px -2px 4px rgba(0,0,0,0.4), 0 24px 60px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.4)`;

  const transitionStyle = animationsEnabled
    ? 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
    : 'none';

  // Public channels hook
  const {
    channels: publicChannels,
    messages: publicMessages,
    activeChannel: publicActiveChannel,
    setActiveChannel: setPublicActiveChannel,
    sendMessage,
    isAdmin,
  } = usePublicChannels();

  // Personal rooms hook
  const {
    myRoom,
    invitedRooms,
    subRooms,
    loading: roomsLoading,
    createRoom,
    loadSubRooms,
  } = usePersonalRooms(currentUser?.id || null);

  // Personal room UI state
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [activeSubRoom, setActiveSubRoom] = useState<string | null>(null);

  // DM Messages hook
  const {
    conversations: dmConversations,
    activeConversation: activeDMConversation,
    messages: dmMessages,
    loading: dmLoading,
    setActiveConversation: setActiveDMConversation,
    getOrCreateConversation,
    sendMessage: sendDMMessage,
    markAsRead: markDMAsRead,
    blockUser,
    unblockUser,
  } = useDMMessages(currentUser?.id || null);

  // DM UI state
  const [dmInputValue, setDMInputValue] = useState('');
  const [showDMChat, setShowDMChat] = useState(false);
  const dmChatRef = useRef<HTMLDivElement>(null);

  // Profile hook - current user's profile
  const {
    profile: myProfile,
    loading: profileLoading,
    updateProfile,
    uploadAvatar,
    getProfile,
  } = useProfile(currentUser?.id || null);

  // Profile viewing state
  const [viewingProfile, setViewingProfile] = useState<Profile | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Notifications hook
  const {
    notifications,
    loading: notificationsLoading,
    unreadCount,
    markAsRead: markNotificationAsRead,
    markAllAsRead: markAllNotificationsAsRead,
  } = useNotifications(currentUser?.id || '');

  // Friend requests hook (passing null for toast, we'll handle UI feedback inline)
  const {
    loading: friendRequestLoading,
    sendRequest: sendFriendRequest,
    acceptRequest: acceptFriendRequest,
    rejectRequest: rejectFriendRequest,
    cancelRequest: cancelFriendRequest,
  } = useFriendRequests(currentUser?.id);

  // Friends list state (from Supabase)
  const [friendsList, setFriendsList] = useState<Array<{
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    status: 'online' | 'offline' | 'away' | 'dnd';
    friendship_id: string;
  }>>([]);
  const [friendsLoading, setFriendsLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState<Array<{
    id: string;
    user_id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    created_at: string;
  }>>([]);
  const [pendingSentRequests, setPendingSentRequests] = useState<Array<{
    id: string;
    friend_id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    created_at: string;
  }>>([]);

  // Load friends list from Supabase
  const loadFriendsList = useCallback(async () => {
    if (!currentUser?.id) {
      setFriendsList([]);
      setFriendsLoading(false);
      return;
    }

    setFriendsLoading(true);
    try {
      // Get accepted friendships where user is either user_id or friend_id
      const { data: friendships, error: friendshipsError } = await supabase
        .from('friendships')
        .select('id, user_id, friend_id, status')
        .eq('status', 'accepted')
        .or(`user_id.eq.${currentUser.id},friend_id.eq.${currentUser.id}`);

      if (friendshipsError) {
        console.error('Error loading friendships:', friendshipsError);
        return;
      }

      if (!friendships || friendships.length === 0) {
        setFriendsList([]);
        return;
      }

      // Get the friend IDs (the other user in each friendship)
      const friendIds = friendships.map(f =>
        f.user_id === currentUser.id ? f.friend_id : f.user_id
      );

      // Get profiles for all friends
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url, status')
        .in('id', friendIds);

      if (profilesError) {
        console.error('Error loading friend profiles:', profilesError);
        return;
      }

      // Map profiles with friendship data
      const friends = (profiles || []).map(profile => {
        const friendship = friendships.find(f =>
          f.user_id === profile.id || f.friend_id === profile.id
        );
        return {
          id: profile.id,
          username: profile.username || 'Unknown',
          display_name: profile.display_name,
          avatar_url: profile.avatar_url,
          status: (profile.status as 'online' | 'offline' | 'away' | 'dnd') || 'offline',
          friendship_id: friendship?.id || '',
        };
      });

      setFriendsList(friends);
    } catch (err) {
      console.error('Error in loadFriendsList:', err);
    } finally {
      setFriendsLoading(false);
    }
  }, [currentUser?.id]);

  // Remove friend function
  const removeFriend = useCallback(async (friendId: string) => {
    if (!currentUser?.id) return false;
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .or(`and(user_id.eq.${currentUser.id},friend_id.eq.${friendId}),and(user_id.eq.${friendId},friend_id.eq.${currentUser.id})`);

      if (error) {
        console.error('Error removing friend:', error);
        return false;
      }
      // Refresh friends list
      loadFriendsList();
      return true;
    } catch (err) {
      console.error('Error in removeFriend:', err);
      return false;
    }
  }, [currentUser?.id, loadFriendsList]);

  // Load pending friend requests (incoming)
  const loadPendingRequests = useCallback(async () => {
    if (!currentUser?.id) {
      setPendingRequests([]);
      return;
    }

    try {
      // Get pending requests where current user is the recipient (friend_id)
      const { data: requests, error: requestsError } = await supabase
        .from('friendships')
        .select('id, user_id, created_at')
        .eq('friend_id', currentUser.id)
        .eq('status', 'pending');

      if (requestsError) {
        console.error('Error loading pending requests:', requestsError);
        return;
      }

      if (!requests || requests.length === 0) {
        setPendingRequests([]);
        return;
      }

      // Get profiles for request senders
      const senderIds = requests.map(r => r.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .in('id', senderIds);

      const enrichedRequests = requests.map(req => {
        const profile = profiles?.find(p => p.id === req.user_id);
        return {
          id: req.id,
          user_id: req.user_id,
          username: profile?.username || 'Unknown',
          display_name: profile?.display_name,
          avatar_url: profile?.avatar_url,
          created_at: req.created_at,
        };
      });

      setPendingRequests(enrichedRequests);
    } catch (err) {
      console.error('Error in loadPendingRequests:', err);
    }
  }, [currentUser?.id]);

  // Load pending sent friend requests (outgoing)
  const loadPendingSentRequests = useCallback(async () => {
    if (!currentUser?.id) {
      setPendingSentRequests([]);
      return;
    }

    try {
      // Get pending requests where current user is the sender (user_id)
      const { data: requests, error: requestsError } = await supabase
        .from('friendships')
        .select('id, friend_id, created_at')
        .eq('user_id', currentUser.id)
        .eq('status', 'pending');

      if (requestsError) {
        console.error('Error loading pending sent requests:', requestsError);
        return;
      }

      if (!requests || requests.length === 0) {
        setPendingSentRequests([]);
        return;
      }

      // Get profiles for request recipients
      const recipientIds = requests.map(r => r.friend_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .in('id', recipientIds);

      const enrichedRequests = requests.map(req => {
        const profile = profiles?.find(p => p.id === req.friend_id);
        return {
          id: req.id,
          friend_id: req.friend_id,
          username: profile?.username || 'Unknown',
          display_name: profile?.display_name,
          avatar_url: profile?.avatar_url,
          created_at: req.created_at,
        };
      });

      setPendingSentRequests(enrichedRequests);
    } catch (err) {
      console.error('Error in loadPendingSentRequests:', err);
    }
  }, [currentUser?.id]);

  // Load friends and pending requests on mount/user change
  useEffect(() => {
    loadFriendsList();
    loadPendingRequests();
    loadPendingSentRequests();
  }, [loadFriendsList, loadPendingRequests, loadPendingSentRequests]);

  // Handle viewing a user's profile
  const handleViewProfile = async (userId: string) => {
    const profile = await getProfile(userId);
    if (profile) {
      setViewingProfile(profile);
      setShowProfileModal(true);
    }
  };

  // Room invite code state
  const [roomCodeInput, setRoomCodeInput] = useState('');
  const [roomCodeError, setRoomCodeError] = useState<string | null>(null);
  const [generatedRoomCode, setGeneratedRoomCode] = useState<string | null>(null);
  const [showShareCodeModal, setShowShareCodeModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Generate a room invite code (uses first 8 chars of room ID + random suffix)
  const generateRoomCode = (roomId: string): string => {
    const prefix = roomId.replace(/-/g, '').slice(0, 6).toUpperCase();
    const suffix = Math.random().toString(36).slice(2, 5).toUpperCase();
    return `${prefix}-${suffix}`;
  };

  // Share room code
  const handleShareRoomCode = () => {
    if (myRoom) {
      const code = generateRoomCode(myRoom.id);
      setGeneratedRoomCode(code);
      setShowShareCodeModal(true);
      setCopiedCode(false);
    }
  };

  // Copy code to clipboard
  const handleCopyCode = async () => {
    if (generatedRoomCode) {
      try {
        await navigator.clipboard.writeText(generatedRoomCode);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Join room via code (simplified - in production you'd have a lookup table)
  const handleJoinRoomByCode = async () => {
    if (!roomCodeInput.trim()) {
      setRoomCodeError('Please enter a room code');
      return;
    }

    setRoomCodeError(null);

    // Extract the room prefix from code (first 6 chars)
    const codePrefix = roomCodeInput.replace(/-/g, '').slice(0, 6).toLowerCase();

    // Search for matching room in invited rooms or all accessible rooms
    // For now, we'll show a success message and clear the input
    // In production, you'd query a room_codes table that maps codes to room IDs

    // Check if it matches any invited room
    const matchingRoom = invitedRooms.find(room => {
      const roomPrefix = room.id.replace(/-/g, '').slice(0, 6).toLowerCase();
      return roomPrefix === codePrefix;
    });

    if (matchingRoom) {
      // Already invited - just show success
      setRoomCodeInput('');
      setRoomCodeError('You already have access to this room!');
      setTimeout(() => setRoomCodeError(null), 3000);
    } else {
      // In production: lookup code in database and add user to invited_users
      // For now, show that we're looking for the room
      setRoomCodeError('Room not found. Ask your friend for a valid code.');
      setTimeout(() => setRoomCodeError(null), 3000);
    }
  };

  // Media player state
  interface MediaItem {
    id: string;
    url: string;
    type: 'youtube' | 'video' | 'audio';
    title: string;
    videoId?: string; // For YouTube
  }
  const [mediaUrlInput, setMediaUrlInput] = useState('');
  const [currentMedia, setCurrentMedia] = useState<MediaItem | null>(null);
  const [mediaQueue, setMediaQueue] = useState<MediaItem[]>([]);
  const [isMediaPlaying, setIsMediaPlaying] = useState(false);
  const [mediaProgress, setMediaProgress] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const youtubePlayerRef = useRef<any>(null);

  // Media error state
  const [mediaError, setMediaError] = useState<string | null>(null);

  // YouTube search state
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    videoId: string;
    title: string;
    author: string;
    lengthSeconds: number;
    thumbnail: string;
  }>>([]);
  const [isSearching, setIsSearching] = useState(false);

  // YouTube search via Invidious/Piped API (no API key needed)
  // Uses fallback endpoints since free instances are often unreliable
  const searchYouTube = async (query: string) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setMediaError(null);

    // List of Invidious/Piped API endpoints to try (in order of reliability)
    const endpoints = [
      { url: 'https://vid.puffyan.us/api/v1/search', type: 'invidious' },
      { url: 'https://invidious.privacydev.net/api/v1/search', type: 'invidious' },
      { url: 'https://pipedapi.kavin.rocks/search', type: 'piped' },
    ];

    let lastError: Error | null = null;

    for (const endpoint of endpoints) {
      try {
        const searchUrl = endpoint.type === 'invidious'
          ? `${endpoint.url}?q=${encodeURIComponent(query)}&type=video&page=1`
          : `${endpoint.url}?q=${encodeURIComponent(query)}&filter=videos`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout

        const response = await fetch(searchUrl, {
          headers: { 'Accept': 'application/json' },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        // Parse based on API type
        let results;
        if (endpoint.type === 'invidious') {
          results = data
            .filter((item: any) => item.type === 'video')
            .slice(0, 8)
            .map((item: any) => ({
              videoId: item.videoId,
              title: item.title,
              author: item.author,
              lengthSeconds: item.lengthSeconds || 0,
              thumbnail: item.videoThumbnails?.find((t: any) => t.quality === 'medium')?.url ||
                         `https://i.ytimg.com/vi/${item.videoId}/mqdefault.jpg`,
            }));
        } else {
          // Piped API format
          results = (data.items || [])
            .filter((item: any) => item.type === 'stream')
            .slice(0, 8)
            .map((item: any) => ({
              videoId: item.url?.replace('/watch?v=', '') || '',
              title: item.title,
              author: item.uploaderName,
              lengthSeconds: item.duration || 0,
              thumbnail: item.thumbnail || `https://i.ytimg.com/vi/${item.url?.replace('/watch?v=', '')}/mqdefault.jpg`,
            }))
            .filter((item: any) => item.videoId);
        }

        if (results.length > 0) {
          setSearchResults(results);
          setIsSearching(false);
          return; // Success!
        }
      } catch (error) {
        console.warn(`YouTube search failed with ${endpoint.url}:`, error);
        lastError = error as Error;
        // Continue to next endpoint
      }
    }

    // All endpoints failed
    console.error('All YouTube search endpoints failed:', lastError);
    setMediaError('Search unavailable. Paste a YouTube URL directly (youtube.com/watch?v=...)');
    setSearchResults([]);
    setIsSearching(false);
  };

  // Format video duration from seconds
  const formatVideoDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle selecting a search result
  const handleSelectSearchResult = (result: typeof searchResults[0]) => {
    const media: MediaItem = {
      id: Date.now().toString(),
      url: `https://youtube.com/watch?v=${result.videoId}`,
      type: 'youtube',
      title: result.title,
      videoId: result.videoId,
    };

    if (!currentMedia) {
      setCurrentMedia(media);
      setIsMediaPlaying(true);
    } else {
      setMediaQueue(prev => [...prev, media]);
    }

    // Clear search state
    setSearchResults([]);
    setSearchQuery('');
    setIsSearchMode(false);
  };

  // Parse media URL to detect type
  const parseMediaUrl = (url: string): MediaItem | null => {
    try {
      const urlObj = new URL(url);

      // Check if it's a YouTube URL
      const isYouTube = urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be');

      if (isYouTube) {
        // YouTube video detection - multiple formats
        // Standard: youtube.com/watch?v=VIDEO_ID
        // Short: youtu.be/VIDEO_ID
        // Embed: youtube.com/embed/VIDEO_ID
        // Shorts: youtube.com/shorts/VIDEO_ID
        const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);

        if (youtubeMatch) {
          return {
            id: Date.now().toString(),
            url,
            type: 'youtube',
            title: `YouTube Video`,
            videoId: youtubeMatch[1],
          };
        }

        // YouTube channel/playlist URLs that can't be embedded
        if (urlObj.pathname.includes('/channel/') ||
            urlObj.pathname.includes('/c/') ||
            urlObj.pathname.includes('/@') ||
            urlObj.pathname.includes('/user/') ||
            urlObj.pathname.includes('/playlist')) {
          setMediaError('YouTube channels and playlists cannot be embedded. Please paste a video link (e.g., youtube.com/watch?v=...)');
          return null;
        }

        setMediaError('Could not find video ID in YouTube URL. Use a video link like youtube.com/watch?v=...');
        return null;
      }

      // Direct video file detection
      const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.m4v'];
      const isVideoFile = videoExtensions.some(ext => urlObj.pathname.toLowerCase().endsWith(ext));
      if (isVideoFile) {
        const filename = urlObj.pathname.split('/').pop() || 'Video';
        return {
          id: Date.now().toString(),
          url,
          type: 'video',
          title: filename,
        };
      }

      // Audio file detection
      const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.flac'];
      const isAudioFile = audioExtensions.some(ext => urlObj.pathname.toLowerCase().endsWith(ext));
      if (isAudioFile) {
        const filename = urlObj.pathname.split('/').pop() || 'Audio';
        return {
          id: Date.now().toString(),
          url,
          type: 'audio',
          title: filename,
        };
      }

      // Default to video for unknown URLs (might be direct stream)
      return {
        id: Date.now().toString(),
        url,
        type: 'video',
        title: 'Media',
      };
    } catch {
      setMediaError('Invalid URL. Please enter a valid video or YouTube link.');
      return null;
    }
  };

  // Add media to queue or play immediately
  const handleAddMedia = () => {
    if (!mediaUrlInput.trim()) return;

    setMediaError(null); // Clear any previous errors
    const media = parseMediaUrl(mediaUrlInput.trim());
    if (!media) {
      // Error already set by parseMediaUrl
      return;
    }

    if (!currentMedia) {
      // Play immediately if nothing is playing
      setCurrentMedia(media);
      setIsMediaPlaying(true);
    } else {
      // Add to queue
      setMediaQueue(prev => [...prev, media]);
    }

    setMediaUrlInput('');
    setMediaError(null);
  };

  // Play next in queue
  const handleMediaNext = () => {
    if (mediaQueue.length > 0) {
      const [next, ...rest] = mediaQueue;
      setCurrentMedia(next);
      setMediaQueue(rest);
      setIsMediaPlaying(true);
      setMediaProgress(0);
    } else {
      setCurrentMedia(null);
      setIsMediaPlaying(false);
    }
  };

  // Play previous (restart current for now)
  const handleMediaPrev = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    setMediaProgress(0);
  };

  // Toggle play/pause
  const handleMediaPlayPause = () => {
    if (currentMedia?.type === 'youtube') {
      // YouTube player control handled by iframe API
      setIsMediaPlaying(!isMediaPlaying);
    } else if (videoRef.current) {
      if (isMediaPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsMediaPlaying(!isMediaPlaying);
    }
  };

  // Remove from queue
  const handleRemoveFromQueue = (id: string) => {
    setMediaQueue(prev => prev.filter(m => m.id !== id));
  };

  // Clear current media
  const handleClearMedia = () => {
    setCurrentMedia(null);
    setIsMediaPlaying(false);
    setMediaProgress(0);
    setMediaDuration(0);
  };

  // Format time for display
  const formatMediaTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Scroll to bottom of DM chat when messages change
  useEffect(() => {
    if (dmChatRef.current) {
      dmChatRef.current.scrollTop = dmChatRef.current.scrollHeight;
    }
  }, [dmMessages]);

  // Populate profile editing fields when myProfile loads
  useEffect(() => {
    if (myProfile) {
      setProfileDisplayName(myProfile.display_name || '');
      setProfileUsername(myProfile.username || '');
      setProfileBio(myProfile.bio || '');
      setProfileStatus(myProfile.custom_status || '');
    }
  }, [myProfile]);

  // Handle avatar file upload
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProfileSaving(true);
    try {
      const result = await uploadAvatar(file);
      if (result) {
        setProfileSaveSuccess(true);
        setTimeout(() => setProfileSaveSuccess(false), 2000);
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
    } finally {
      setProfileSaving(false);
    }
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    setProfileSaving(true);
    setProfileSaveSuccess(false);

    try {
      const success = await updateProfile({
        display_name: profileDisplayName.trim() || undefined,
        bio: profileBio.trim() || undefined,
        custom_status: profileStatus.trim() || undefined,
      });

      if (success) {
        setProfileSaveSuccess(true);
        setTimeout(() => setProfileSaveSuccess(false), 2000);
      }
    } catch (err) {
      console.error('Profile save error:', err);
    } finally {
      setProfileSaving(false);
    }
  };

  // Handle starting a DM with a friend
  const handleStartDM = async (friendId: string) => {
    const conversationId = await getOrCreateConversation(friendId);
    if (conversationId) {
      // Find or create the conversation in local state
      const conv = dmConversations.find(c => c.id === conversationId);
      if (conv) {
        setActiveDMConversation(conv);
        setShowDMChat(true);
      }
    }
  };

  // Handle sending a DM
  const handleSendDM = async () => {
    if (!dmInputValue.trim()) return;
    const success = await sendDMMessage(dmInputValue.trim());
    if (success) {
      setDMInputValue('');
    }
  };

  // Friend calling hook
  const {
    callState,
    activeCall,
    incomingCall,
    isMuted: callIsMuted,
    isDeafened: callIsDeafened,
    isVideoOn,
    localStream,
    remoteStream,
    callDuration,
    error: callError,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute: toggleCallMute,
    toggleDeafen: toggleCallDeafen,
    toggleVideo,
  } = useFriendCall(
    currentUser?.id || null,
    currentUser?.username || 'User',
    currentUser?.avatar_url || null
  );

  // Format call duration
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle starting a call from the friends list
  const handleStartCall = async (friendId: string, friendName: string, friendAvatar: string | null, type: 'voice' | 'video' = 'voice') => {
    await startCall(friendId, friendName, friendAvatar, type);
  };

  // Load sub-rooms when myRoom changes
  useEffect(() => {
    if (myRoom) {
      loadSubRooms(myRoom.id);
    }
  }, [myRoom, loadSubRooms]);

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return;
    const roomId = await createRoom(newRoomName.trim());
    if (roomId) {
      setShowCreateRoomModal(false);
      setNewRoomName('');
    }
  };

  const channels = publicChannels.map(c => ({
    id: c.id,
    name: c.name,
    unread: c.unread_count || 0,
    type: c.type,
    is_admin_only: c.is_admin_only,
  }));

  const activeChannel = publicActiveChannel;
  const messages = publicMessages;
  const [inputValue, setInputValue] = useState('');

  const currentChannelConfig = channels.find(c => c.id === activeChannel);
  const isAdminOnlyChannel = currentChannelConfig?.is_admin_only || false;
  const canSendMessage = !isAdminOnlyChannel || isAdmin;

  // Load islands from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setIslands(parsed);
        }
      }
    } catch (e) {
      console.log('Using default islands');
    }
  }, []);

  // Save islands to localStorage
  const saveIslands = useCallback((newIslands: DynamicIsland[]) => {
    setIslands(newIslands);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newIslands));
    } catch (e) {
      console.error('Failed to save islands');
    }
  }, []);

  // Update island position
  const updateIslandPosition = useCallback((type: IslandType, x: number, y: number) => {
    if (isLayoutLocked) return;
    const newIslands = islands.map(island =>
      island.type === type ? { ...island, position: { ...island.position, x, y } } : island
    );
    saveIslands(newIslands);
  }, [islands, saveIslands, isLayoutLocked]);

  // Update island size
  const updateIslandSize = useCallback((type: IslandType, width: number, height: number, x: number, y: number) => {
    if (isLayoutLocked) return;
    const newIslands = islands.map(island =>
      island.type === type ? { ...island, position: { x, y, width, height } } : island
    );
    saveIslands(newIslands);
  }, [islands, saveIslands, isLayoutLocked]);

  // Toggle island visibility
  const toggleIsland = useCallback((type: IslandType) => {
    const existing = islands.find(i => i.type === type);
    if (existing) {
      // Toggle visibility
      const newIslands = islands.map(island =>
        island.type === type ? { ...island, visible: !island.visible } : island
      );
      saveIslands(newIslands);
    } else {
      // Add new island
      const config = ISLAND_REGISTRY[type];
      const newIsland: DynamicIsland = {
        type,
        position: {
          x: 100 + Math.random() * 200,
          y: 100 + Math.random() * 200,
          width: config.defaultWidth,
          height: config.defaultHeight,
        },
        visible: true,
      };
      saveIslands([...islands, newIsland]);
    }
  }, [islands, saveIslands]);

  // Toggle individual island lock
  const toggleIslandLock = useCallback((type: IslandType) => {
    const newIslands = islands.map(island =>
      island.type === type ? { ...island, locked: !island.locked } : island
    );
    saveIslands(newIslands);
  }, [islands, saveIslands]);

  // Reset layout to default positions and sizes
  const resetLayout = useCallback(() => {
    saveIslands(DEFAULT_ISLANDS);
    setIsLayoutLocked(false);
  }, [saveIslands]);

  // Snap islands together with gaps - animated! Auto-scales if too big, wraps rows, respects bounds.
  // Locked islands stay in place, others snap around them.
  const snapIslands = useCallback(() => {
    const visibleIslands = islands.filter(i => i.visible);
    if (visibleIslands.length === 0 || isSnapping) return;

    // Separate locked and unlocked islands
    const lockedIslands = visibleIslands.filter(i => i.locked);
    const unlockedIslands = visibleIslands.filter(i => !i.locked);

    // If all islands are locked, nothing to do
    if (unlockedIslands.length === 0) return;

    // Sort unlocked by x position (maintain relative order)
    const sorted = [...unlockedIslands].sort((a, b) => a.position.x - b.position.x);

    const startX = 40;
    const startY = 80;
    const maxWidth = window.innerWidth - 60; // Right wall
    const maxHeight = window.innerHeight - 60; // Bottom wall

    // Calculate available space (excluding locked islands' areas)
    // For simplicity, we calculate based on remaining width after locked islands
    const lockedWidth = lockedIslands.reduce((sum, i) => sum + i.position.width + GAP, 0);
    const availableWidth = maxWidth - startX - lockedWidth;
    const availableHeight = maxHeight - startY;

    // Calculate total width needed for unlocked islands
    const totalUnlockedWidth = sorted.reduce((sum, i) => sum + i.position.width, 0) + (sorted.length - 1) * GAP;
    const maxIslandHeight = Math.max(...sorted.map(i => i.position.height));

    // Calculate scale factor if unlocked islands are too big
    let scaleFactor = 1;

    if (totalUnlockedWidth > availableWidth && availableWidth > 0) {
      scaleFactor = Math.min(scaleFactor, availableWidth / totalUnlockedWidth);
    }

    if (maxIslandHeight > availableHeight) {
      scaleFactor = Math.min(scaleFactor, availableHeight / maxIslandHeight);
    }

    // Don't scale below 50% of original size
    scaleFactor = Math.max(scaleFactor, 0.5);

    // Find positions that don't overlap with locked islands
    const isOverlappingLocked = (x: number, y: number, w: number, h: number) => {
      return lockedIslands.some(locked => {
        const lx = locked.position.x;
        const ly = locked.position.y;
        const lw = locked.position.width;
        const lh = locked.position.height;
        return !(x + w + GAP <= lx || x >= lx + lw + GAP || y + h + GAP <= ly || y >= ly + lh + GAP);
      });
    };

    // Calculate positions with scaled sizes, avoiding locked islands
    let currentX = startX;
    let currentY = startY;
    let rowMaxHeight = 0;

    const positionMap = new Map<IslandType, { x: number; y: number; width: number; height: number }>();

    sorted.forEach((island) => {
      const config = ISLAND_REGISTRY[island.type];

      // Apply scale factor, but respect minimum sizes
      const scaledWidth = Math.max(config.minWidth, Math.round(island.position.width * scaleFactor));
      const scaledHeight = Math.max(config.minHeight, Math.round(island.position.height * scaleFactor));

      // Skip over locked island positions
      while (isOverlappingLocked(currentX, currentY, scaledWidth, scaledHeight) && currentX < maxWidth) {
        currentX += 50; // Jump ahead
        if (currentX + scaledWidth > maxWidth) {
          currentX = startX;
          currentY += rowMaxHeight + GAP;
          rowMaxHeight = 0;
        }
      }

      // Check if this island would go off right side
      if (currentX + scaledWidth > maxWidth && currentX > startX) {
        currentX = startX;
        currentY += rowMaxHeight + GAP;
        rowMaxHeight = 0;
      }

      // Clamp Y so island doesn't go below bottom wall
      let finalY = currentY;
      if (finalY + scaledHeight > maxHeight) {
        finalY = Math.max(startY, maxHeight - scaledHeight);
      }

      positionMap.set(island.type, { x: currentX, y: finalY, width: scaledWidth, height: scaledHeight });

      currentX += scaledWidth + GAP;
      rowMaxHeight = Math.max(rowMaxHeight, scaledHeight);
    });

    // Calculate new positions and sizes (locked islands unchanged)
    const newIslands = islands.map(island => {
      if (!island.visible) return island;

      // Locked islands don't move
      if (island.locked) return island;

      const pos = positionMap.get(island.type);
      if (!pos) return island;

      return {
        ...island,
        position: {
          x: pos.x,
          y: pos.y,
          width: pos.width,
          height: pos.height,
        },
      };
    });

    // 1. Enable snapping mode (switches to CSS-animated divs)
    setIsSnapping(true);

    // 2. After a frame, update positions (transition will animate)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        saveIslands(newIslands);

        // 3. After animation completes, switch back to Rnd
        setTimeout(() => setIsSnapping(false), 550);
      });
    });
  }, [islands, saveIslands, isSnapping]);

  // Handle channel selection
  const handleChannelSelect = (channelId: string) => {
    setPublicActiveChannel(channelId);
    if (onChannelSelect) onChannelSelect(channelId);
  };

  // Handle sending a message
  const handleSend = async () => {
    if (!inputValue.trim() || !canSendMessage) return;
    const success = await sendMessage(inputValue.trim());
    if (success) setInputValue('');
  };

  const activeMessages = messages[activeChannel] || [];
  const visibleIslands = islands.filter(i => i.visible);

  // ============== ISLAND CONTENT RENDERERS ==============
  const renderIslandContent = (type: IslandType) => {
    switch (type) {
      case 'navigation':
        return (
          <>
            <div className="drag-handle flex items-center justify-between mb-3 cursor-move">
              <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-gray-400">Navigation</div>
              <DragHandle />
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto scrollbar-thin scrollbar-thumb-cyan-500/20">
              {/* Public Channels Section */}
              <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 px-1">Public Channels</div>
              {channels.map((channel, i) => {
                const isActive = channel.id === activeChannel;
                const isAnnouncement = channel.is_admin_only;
                return (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelSelect(channel.id)}
                    className={`w-full p-3 rounded-xl text-left border transition-all duration-200 ${
                      isActive
                        ? isAnnouncement
                          ? 'bg-gradient-to-r from-amber-500/20 to-transparent border-l-2 border-amber-400 text-amber-100 border-white/10'
                          : 'bg-gradient-to-r from-cyan-500/20 to-transparent border-l-2 border-cyan-400 text-cyan-100 border-white/10'
                        : 'bg-white/0 border-white/10 hover:bg-cyan-500/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-mono text-slate-400">0{i + 1}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-mono">#{channel.name}</span>
                        {channel.unread > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-400/40">{channel.unread}</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* Divider */}
              <div className="h-px bg-white/10 my-2" />

              {/* Personal Room Section */}
              <div className="flex items-center justify-between px-1">
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500">Your Room</div>
                {!myRoom && !roomsLoading && (
                  <button
                    onClick={() => setShowCreateRoomModal(true)}
                    className="w-5 h-5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/30 flex items-center justify-center transition-all"
                    title="Create your room"
                  >
                    <div className="w-2.5 h-0.5 bg-cyan-400 absolute" />
                    <div className="w-0.5 h-2.5 bg-cyan-400 absolute" />
                  </button>
                )}
              </div>

              {roomsLoading ? (
                <div className="text-center text-slate-500 text-xs py-2">Loading...</div>
              ) : myRoom ? (
                <div className="space-y-1">
                  <div className="p-2 rounded-xl bg-purple-500/10 border border-purple-400/20">
                    <div className="text-sm font-medium text-purple-200 mb-2">{myRoom.name}</div>
                    {subRooms.map((sr) => (
                      <button
                        key={sr.id}
                        onClick={() => setActiveSubRoom(sr.id)}
                        className={`w-full p-2 rounded-lg text-left text-xs transition-all ${
                          activeSubRoom === sr.id
                            ? 'bg-purple-500/20 text-purple-100'
                            : 'hover:bg-white/5 text-slate-400'
                        }`}
                      >
                        <span className="mr-1">{sr.type === 'chat' ? '#' : sr.type === 'voice' ? '🔊' : '📺'}</span>
                        {sr.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center text-slate-500 text-xs py-4">
                  <p>No room yet</p>
                  <p className="text-[10px] mt-1">Create one to invite friends!</p>
                </div>
              )}

              {/* Invited Rooms */}
              {invitedRooms.length > 0 && (
                <>
                  <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 px-1 mt-2">Invited Rooms</div>
                  {invitedRooms.map((room) => (
                    <div key={room.id} className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-400/20">
                      <div className="text-sm font-medium text-emerald-200">{room.name}</div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Create Room Modal */}
            {showCreateRoomModal && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-3xl flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900/95 border border-white/10 rounded-2xl p-4 w-full max-w-xs">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-white">Create Your Room</h3>
                    <button onClick={() => setShowCreateRoomModal(false)} className="text-gray-400 hover:text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">Give your room a name. You can only have one personal room.</p>
                  <input
                    type="text"
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                    placeholder="My Room"
                    className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50 mb-3"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateRoom()}
                  />
                  <button
                    onClick={handleCreateRoom}
                    disabled={!newRoomName.trim()}
                    className="w-full px-3 py-2 text-sm bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                  >
                    Create Room
                  </button>
                </div>
              </div>
            )}
          </>
        );

      case 'chat':
        return (
          <>
            <div className="drag-handle flex items-center justify-between border-b border-white/10 px-4 py-3 cursor-move">
              <div className="flex items-center gap-3">
                <DragHandle />
                <span className="font-mono text-[11px] text-slate-400 uppercase">Channel</span>
                <span className="text-sm font-semibold text-cyan-100">#{channels.find(c => c.id === activeChannel)?.name || 'Select'}</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin scrollbar-thumb-cyan-500/20">
              {activeMessages.map((msg: any) => (
                <div key={msg.id} className="flex gap-3">
                  <GlassAvatar
                    src={msg.avatar_url}
                    name={msg.author}
                    size="md"
                    onClick={() => msg.author_id && handleViewProfile(msg.author_id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => msg.author_id && handleViewProfile(msg.author_id)}
                        className="font-semibold text-sm hover:text-cyan-300 transition-colors"
                      >
                        {msg.author}
                      </button>
                      {msg.is_admin && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-200">Admin</span>}
                      <span className="text-[10px] text-slate-500">{msg.time}</span>
                    </div>
                    <p className="text-slate-200 text-sm">{msg.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 px-4 py-3">
              {isAdminOnlyChannel && !isAdmin ? (
                <div className="flex items-center justify-center gap-2 text-[11px] text-amber-200/80">
                  {/* Custom lock icon */}
                  <div className="flex flex-col gap-0.5">
                    <div className="w-2.5 h-0.5 rounded-full bg-amber-400/60" />
                    <div className="w-2.5 h-0.5 rounded-full bg-amber-400/60" />
                  </div>
                  <span>READ ONLY</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Message..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm"
                  />
                  <button onClick={handleSend} className="px-4 py-2 rounded-xl bg-cyan-500 text-white text-sm">Send</button>
                </div>
              )}
            </div>
            <div className="border-t border-white/10 px-4 py-2 flex items-end gap-1 h-10">
              {FOOTER_BARS.map((h, i) => <div key={i} className="flex-1 bg-cyan-500/40 rounded-t" style={{ height: `${h}%` }} />)}
            </div>
          </>
        );

      case 'controls':
        return (
          <>
            <div className="drag-handle flex items-center justify-between mb-3 cursor-move">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-gray-400">Systems</div>
                <div className="text-sm text-slate-200">Activity & Controls</div>
              </div>
              <DragHandle />
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto">
              {/* User Profile Card */}
              <button
                onClick={() => {
                  if (myProfile) {
                    setViewingProfile(myProfile);
                    setShowProfileModal(true);
                  }
                }}
                className="w-full p-3 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-white/10 hover:border-cyan-400/30 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <GlassAvatar
                    src={myProfile?.avatar_url}
                    name={myProfile?.display_name || currentUser?.email?.split('@')[0] || 'User'}
                    size="lg"
                    status={myProfile?.status || 'online'}
                  />
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate group-hover:text-cyan-200 transition-colors">
                      {myProfile?.display_name || currentUser?.email?.split('@')[0] || 'User'}
                    </div>
                    <div className="text-[10px] text-slate-400 truncate">
                      {myProfile?.custom_status || 'Click to edit profile'}
                    </div>
                  </div>
                  <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                </div>
              </button>

              {/* Island Control Button */}
              <button
                onClick={() => setShowPlanetModal(true)}
                className="w-full p-4 rounded-2xl bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-400/30 hover:border-purple-400/60 transition-all group"
              >
                <div className="flex items-center gap-3">
                  {/* Custom planet/orbit icon */}
                  <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-110 transition-transform relative">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-purple-400/60 to-cyan-400/60" />
                    <div className="absolute inset-1 border border-cyan-400/30 rounded-full" />
                    <div className="absolute w-1.5 h-1.5 rounded-full bg-cyan-400/60 top-1 right-2" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold text-purple-200">Island Control</div>
                    <div className="text-[10px] text-slate-400">Add, remove, and arrange islands</div>
                  </div>
                </div>
              </button>

              {/* Status */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                  <span>System Status</span>
                  <div className="flex items-center gap-1">
                    <div className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)]" />
                    <span className="text-emerald-300">Online</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>Active Islands</span>
                  <span className="text-cyan-300">{visibleIslands.length}</span>
                </div>
              </div>

              {/* Background controls */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3 space-y-3">
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>Background FX</span>
                  <span className="text-cyan-200">{Math.round(bgIntensity * 100)}%</span>
                </div>
                <input type="range" min={0.3} max={1} step={0.05} value={bgIntensity} onChange={(e) => setBgIntensity(Number(e.target.value))} className="w-full accent-cyan-400" />
              </div>

              {/* Glass opacity */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-3 space-y-3">
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>Glass Opacity</span>
                  <span className="text-cyan-200">{Math.round(panelOpacity * 100)}%</span>
                </div>
                <input type="range" min={0.3} max={1} step={0.05} value={panelOpacity} onChange={(e) => setPanelOpacity(Number(e.target.value))} className="w-full accent-cyan-400" />
              </div>
            </div>
          </>
        );

      case 'friends':
        return (
          <>
            <div className="drag-handle flex items-center justify-between mb-3 cursor-move">
              <div className="flex items-center gap-2">
                {/* Friends icon - overlapping circles */}
                <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <div className="flex -space-x-0.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-cyan-400/60 border border-white/20" />
                    <div className="w-2.5 h-2.5 rounded-full bg-cyan-400/40 border border-white/20" />
                  </div>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-gray-400">
                  {showDMChat && activeDMConversation ? 'DM' : 'Friends'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {showDMChat && (
                  <button
                    onClick={() => {
                      setShowDMChat(false);
                      setActiveDMConversation(null);
                    }}
                    className="w-5 h-5 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    title="Back to friends"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <DragHandle />
              </div>
            </div>

            {/* DM Chat View */}
            {showDMChat && activeDMConversation ? (
              <div className="flex-1 flex flex-col min-h-0">
                {/* Chat Header */}
                <div className="flex items-center gap-2 p-2 mb-2 rounded-lg bg-white/5 border border-white/10">
                  <div className="w-8 h-8 rounded-full bg-purple-500/30 flex items-center justify-center text-xs">
                    {activeDMConversation.other_user?.username?.[0] || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{activeDMConversation.other_user?.username}</span>
                    <span className={`text-[10px] ${activeDMConversation.other_user?.status === 'online' ? 'text-emerald-400' : 'text-gray-500'}`}>
                      {activeDMConversation.other_user?.status === 'online' ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  {/* Call buttons */}
                  <button
                    onClick={() => activeDMConversation.other_user && handleStartCall(
                      activeDMConversation.other_user.id,
                      activeDMConversation.other_user.username,
                      activeDMConversation.other_user.avatar_url,
                      'voice'
                    )}
                    disabled={callState !== 'idle'}
                    className="w-7 h-7 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Voice call"
                  >
                    <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => activeDMConversation.other_user && handleStartCall(
                      activeDMConversation.other_user.id,
                      activeDMConversation.other_user.username,
                      activeDMConversation.other_user.avatar_url,
                      'video'
                    )}
                    disabled={callState !== 'idle'}
                    className="w-7 h-7 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Video call"
                  >
                    <svg className="w-3.5 h-3.5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>

                {/* Messages */}
                <div ref={dmChatRef} className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-thin scrollbar-thumb-cyan-500/20">
                  {dmMessages.length > 0 ? dmMessages.map((msg) => {
                    const isMe = msg.sender_id === currentUser?.id;
                    return (
                      <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs ${
                          isMe
                            ? 'bg-cyan-500/20 border border-cyan-400/30 text-cyan-100'
                            : 'bg-white/10 border border-white/10 text-gray-200'
                        }`}>
                          <p>{msg.content}</p>
                          <span className="text-[9px] text-gray-500 mt-1 block">
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="text-center text-slate-500 py-8">
                      <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <p className="text-xs">No messages yet</p>
                      <p className="text-[10px] text-gray-600 mt-1">Start the conversation!</p>
                    </div>
                  )}
                </div>

                {/* Input */}
                <div className="mt-2 flex gap-2">
                  <input
                    type="text"
                    value={dmInputValue}
                    onChange={(e) => setDMInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendDM()}
                    placeholder="Message..."
                    className="flex-1 px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50"
                  />
                  <button
                    onClick={handleSendDM}
                    disabled={!dmInputValue.trim()}
                    className="px-3 py-2 text-xs bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                  >
                    Send
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Search Bar */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    value={friendSearchQuery}
                    onChange={(e) => setFriendSearchQuery(e.target.value)}
                    placeholder="Search friends..."
                    className="w-full px-3 py-2 pl-8 text-xs bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50"
                  />
                  <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {/* Add Friend Button */}
                <button
                  onClick={() => setShowAddFriendModal(true)}
                  className="w-full mb-3 px-3 py-2 text-xs bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/30 rounded-lg text-cyan-300 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Friend
                </button>

                {/* Recent Conversations */}
                {dmConversations.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-2">Recent Messages</div>
                    <div className="space-y-1">
                      {dmConversations.slice(0, 3).map((conv) => (
                        <button
                          key={conv.id}
                          onClick={() => {
                            setActiveDMConversation(conv);
                            setShowDMChat(true);
                          }}
                          className="w-full flex items-center gap-2 p-2 rounded-lg border border-purple-400/20 bg-purple-500/10 hover:bg-purple-500/20 transition-colors text-left"
                        >
                          <div className="w-6 h-6 rounded-full bg-purple-500/30 flex items-center justify-center text-[10px]">
                            {conv.other_user?.username?.[0] || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs truncate block text-purple-200">{conv.other_user?.username}</span>
                            {conv.last_message && (
                              <span className="text-[10px] text-gray-500 truncate block">{conv.last_message}</span>
                            )}
                          </div>
                          {(conv.unread_count || 0) > 0 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500 text-white">
                              {conv.unread_count}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Friends List */}
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                  Friends {friendsList.length > 0 && `(${friendsList.length})`}
                </div>
                <div className="flex-1 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-cyan-500/20">
                  {friendsLoading ? (
                    <div className="text-center text-slate-500 py-6">
                      <p className="text-xs">Loading friends...</p>
                    </div>
                  ) : friendsList.length > 0 ? friendsList
                    .filter((f) => !friendSearchQuery || f.username?.toLowerCase().includes(friendSearchQuery.toLowerCase()))
                    .map((friend) => (
                    <div
                      key={friend.id}
                      className="flex items-center gap-2 p-2 rounded-xl border border-white/10 hover:bg-white/5 transition-colors group cursor-pointer"
                      onContextMenu={(e) => handleUserContextMenu(e, {
                        id: friend.id,
                        username: friend.username,
                        display_name: friend.display_name,
                        avatar_url: friend.avatar_url,
                        isFriend: true,
                      })}
                    >
                      <GlassAvatar
                        src={friend.avatar_url}
                        name={friend.display_name || friend.username}
                        size="sm"
                        status={friend.status}
                        onClick={() => handleViewProfile(friend.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm truncate block">{friend.display_name || friend.username}</span>
                        <span className="text-[10px] text-gray-500">
                          @{friend.username} • {friend.status === 'online' ? 'Online' : friend.status === 'away' ? 'Away' : friend.status === 'dnd' ? 'Do Not Disturb' : 'Offline'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* DM Button */}
                        <button
                          onClick={() => handleStartDM(friend.id)}
                          className="w-6 h-6 rounded bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 flex items-center justify-center transition-colors"
                          title="Message"
                        >
                          <svg className="w-3 h-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </button>
                        {/* Call Button */}
                        <button
                          onClick={() => handleStartCall(friend.id, friend.username, friend.avatar_url, 'voice')}
                          disabled={callState !== 'idle'}
                          className="w-6 h-6 rounded bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 flex items-center justify-center transition-colors disabled:opacity-50"
                          title="Call"
                        >
                          <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </button>
                      </div>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        friend.status === 'online' ? 'bg-emerald-400' :
                        friend.status === 'away' ? 'bg-amber-400' :
                        friend.status === 'dnd' ? 'bg-red-400' : 'bg-slate-500'
                      }`} />
                    </div>
                  )) : (
                    <div className="text-center text-slate-500 py-8">
                      <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <p className="text-xs">No friends yet</p>
                      <p className="text-[10px] text-gray-600 mt-1">Add friends to see them here</p>
                    </div>
                  )}
                </div>

                {/* Add Friend Modal */}
                {showAddFriendModal && (
                  <AddFriendModal
                    isOpen={showAddFriendModal}
                    onClose={() => {
                      setShowAddFriendModal(false);
                      setFriendCodeInput('');
                    }}
                    onSendRequest={async (userId, username) => {
                      const result = await sendFriendRequest(userId, username);
                      if (result.success) {
                        setShowAddFriendModal(false);
                        setFriendCodeInput('');
                      }
                      return result;
                    }}
                    loading={friendRequestLoading}
                    currentUserId={currentUser?.id}
                  />
                )}
              </>
            )}
          </>
        );

      case 'settings':
        return (
          <>
            <div className="drag-handle flex items-center justify-between mb-3 cursor-move">
              <div className="flex items-center gap-2">
                {/* Settings icon */}
                <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-cyan-400/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-gray-400">Settings</div>
              </div>
              <DragHandle />
            </div>

            {/* Settings Tabs */}
            <div className="flex gap-1 mb-3 p-1 bg-white/5 rounded-lg">
              {(['profile', 'appearance', 'audio', 'layout'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSettingsTab(tab)}
                  className={`flex-1 px-2 py-1.5 text-[10px] font-medium rounded-md transition-colors ${
                    settingsTab === tab
                      ? 'bg-cyan-500/30 text-cyan-300'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto">
              {/* Profile Tab */}
              {settingsTab === 'profile' && (
                <>
                  {/* Avatar Section */}
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-gray-400 mb-3">Profile Picture</div>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <GlassAvatar
                          src={myProfile?.avatar_url}
                          name={myProfile?.display_name || myProfile?.username || 'User'}
                          size="lg"
                        />
                        <button
                          onClick={() => avatarInputRef.current?.click()}
                          className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-cyan-500 hover:bg-cyan-400 flex items-center justify-center transition-colors"
                          disabled={profileSaving}
                        >
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </button>
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                        />
                      </div>
                      <div className="flex-1 text-xs text-slate-400">
                        Click the camera icon to upload a new profile picture
                      </div>
                    </div>
                  </div>

                  {/* Profile Info */}
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-3">
                    <div className="text-xs text-gray-400">Profile Information</div>

                    {/* Display Name */}
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">Display Name</label>
                      <input
                        type="text"
                        value={profileDisplayName}
                        onChange={(e) => setProfileDisplayName(e.target.value)}
                        placeholder="Your display name"
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50"
                      />
                    </div>

                    {/* Username (read-only for now) */}
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">Username</label>
                      <input
                        type="text"
                        value={profileUsername}
                        disabled
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-slate-500 cursor-not-allowed"
                      />
                      <span className="text-[9px] text-slate-500">Username cannot be changed</span>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">Status</label>
                      <input
                        type="text"
                        value={profileStatus}
                        onChange={(e) => setProfileStatus(e.target.value)}
                        placeholder="What are you up to?"
                        maxLength={100}
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50"
                      />
                    </div>

                    {/* Bio */}
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">Bio</label>
                      <textarea
                        value={profileBio}
                        onChange={(e) => setProfileBio(e.target.value)}
                        placeholder="Tell us about yourself..."
                        maxLength={500}
                        rows={3}
                        className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50 resize-none"
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveProfile}
                      disabled={profileSaving}
                      className="flex-1 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {profileSaving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                    {profileSaveSuccess && (
                      <div className="flex items-center gap-1 text-emerald-400 text-xs">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Saved!
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Appearance Tab */}
              {settingsTab === 'appearance' && (
                <>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-gray-400 mb-2">Visual Effects</div>
                    <div className="space-y-2">
                      <label className="flex items-center justify-between text-sm text-slate-200">
                        <span>Glow effects</span>
                        <button
                          onClick={() => setGlowEffects(!glowEffects)}
                          className={`relative w-9 h-5 rounded-full transition-colors ${glowEffects ? 'bg-cyan-500' : 'bg-gray-600'}`}
                        >
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${glowEffects ? 'translate-x-4' : ''}`} />
                        </button>
                      </label>
                      <label className="flex items-center justify-between text-sm text-slate-200">
                        <span>Animations</span>
                        <button
                          onClick={() => setAnimationsEnabled(!animationsEnabled)}
                          className={`relative w-9 h-5 rounded-full transition-colors ${animationsEnabled ? 'bg-cyan-500' : 'bg-gray-600'}`}
                        >
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${animationsEnabled ? 'translate-x-4' : ''}`} />
                        </button>
                      </label>
                      <label className="flex items-center justify-between text-sm text-slate-200">
                        <span>Blur/Glass</span>
                        <button
                          onClick={() => setBlurEffects(!blurEffects)}
                          className={`relative w-9 h-5 rounded-full transition-colors ${blurEffects ? 'bg-cyan-500' : 'bg-gray-600'}`}
                        >
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${blurEffects ? 'translate-x-4' : ''}`} />
                        </button>
                      </label>
                      <label className="flex items-center justify-between text-sm text-slate-200">
                        <span>Background motion</span>
                        <button
                          onClick={() => setBackgroundMotion(!backgroundMotion)}
                          className={`relative w-9 h-5 rounded-full transition-colors ${backgroundMotion ? 'bg-cyan-500' : 'bg-gray-600'}`}
                        >
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${backgroundMotion ? 'translate-x-4' : ''}`} />
                        </button>
                      </label>
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-gray-400 mb-2">UI Style</div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => onSwitchStyle?.('standard')}
                        className="p-2 text-xs rounded-lg border border-white/10 hover:border-cyan-400/50 transition-colors text-gray-300 hover:text-white"
                      >
                        Standard
                      </button>
                      <button
                        onClick={() => onSwitchStyle?.('jarvis')}
                        className="p-2 text-xs rounded-lg border border-cyan-400/50 bg-cyan-500/20 text-cyan-300"
                      >
                        Jarvis
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Audio Tab */}
              {settingsTab === 'audio' && (
                <>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-gray-400 mb-2">Voice Isolation</div>
                    <label className="flex items-center justify-between text-sm text-slate-200">
                      <div>
                        <span>AI Noise Suppression</span>
                        <p className="text-[10px] text-gray-500">DeepFilterNet3</p>
                      </div>
                      <button
                        onClick={() => setNoiseSuppressionEnabled(!noiseSuppressionEnabled)}
                        className={`relative w-9 h-5 rounded-full transition-colors ${noiseSuppressionEnabled ? 'bg-emerald-500' : 'bg-gray-600'}`}
                      >
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${noiseSuppressionEnabled ? 'translate-x-4' : ''}`} />
                      </button>
                    </label>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-gray-400 mb-3">Input Volume</div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={inputVolume}
                      onChange={(e) => setInputVolume(parseInt(e.target.value))}
                      className="w-full h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${inputVolume}%, #374151 ${inputVolume}%, #374151 100%)`
                      }}
                    />
                    <div className="flex justify-between mt-1 text-[10px] text-gray-500">
                      <span>0%</span>
                      <span>{inputVolume}%</span>
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-gray-400 mb-3">Output Volume</div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={outputVolume}
                      onChange={(e) => setOutputVolume(parseInt(e.target.value))}
                      className="w-full h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${outputVolume}%, #374151 ${outputVolume}%, #374151 100%)`
                      }}
                    />
                    <div className="flex justify-between mt-1 text-[10px] text-gray-500">
                      <span>0%</span>
                      <span>{outputVolume}%</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 text-center">
                    Open full settings for device selection
                  </p>
                </>
              )}

              {/* Layout Tab */}
              {settingsTab === 'layout' && (
                <>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-gray-400 mb-2">Island Layout</div>
                    <label className="flex items-center justify-between text-sm text-slate-200">
                      <span>Lock positions</span>
                      <button
                        onClick={() => setIsLayoutLocked(!isLayoutLocked)}
                        className={`relative w-9 h-5 rounded-full transition-colors ${isLayoutLocked ? 'bg-cyan-500' : 'bg-gray-600'}`}
                      >
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${isLayoutLocked ? 'translate-x-4' : ''}`} />
                      </button>
                    </label>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-gray-400 mb-2">Snap Mode</div>
                    <label className="flex items-center justify-between text-sm text-slate-200">
                      <span>Edge snapping</span>
                      <button
                        onClick={() => setIsSnapping(!isSnapping)}
                        className={`relative w-9 h-5 rounded-full transition-colors ${isSnapping ? 'bg-cyan-500' : 'bg-gray-600'}`}
                      >
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${isSnapping ? 'translate-x-4' : ''}`} />
                      </button>
                    </label>
                  </div>
                  <button
                    onClick={resetLayout}
                    className="w-full px-3 py-2 text-xs bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 rounded-lg text-amber-300 transition-colors"
                  >
                    Reset Layout to Default
                  </button>
                </>
              )}
            </div>
          </>
        );

      case 'rooms':
        return (
          <>
            <div className="drag-handle flex items-center justify-between mb-3 cursor-move">
              <div className="flex items-center gap-2">
                {/* Rooms icon - grid */}
                <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                  <div className="grid grid-cols-2 gap-0.5">
                    <div className="w-1.5 h-1.5 rounded-sm bg-cyan-400/60" />
                    <div className="w-1.5 h-1.5 rounded-sm bg-cyan-400/40" />
                    <div className="w-1.5 h-1.5 rounded-sm bg-cyan-400/30" />
                    <div className="w-1.5 h-1.5 rounded-sm bg-cyan-400/20" />
                  </div>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-gray-400">Rooms</div>
              </div>
              <DragHandle />
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto scrollbar-thin scrollbar-thumb-cyan-500/20">
              {/* Your Room Section */}
              {myRoom ? (
                <div className="rounded-2xl border border-purple-400/30 bg-gradient-to-r from-purple-500/10 to-transparent p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 border border-purple-400/30 flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-purple-100">{myRoom.name}</div>
                        <div className="text-[10px] text-purple-300/60">Your Room</div>
                      </div>
                    </div>
                    <button
                      onClick={handleShareRoomCode}
                      className="w-8 h-8 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 flex items-center justify-center transition-all"
                      title="Share invite code"
                    >
                      <svg className="w-4 h-4 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>
                  </div>
                  {/* Sub-rooms */}
                  <div className="space-y-1 pl-2 border-l border-purple-400/20">
                    {subRooms.map((sr) => (
                      <button
                        key={sr.id}
                        onClick={() => setActiveSubRoom(sr.id)}
                        className={`w-full p-2 rounded-lg text-left text-xs flex items-center gap-2 transition-all ${
                          activeSubRoom === sr.id
                            ? 'bg-purple-500/20 text-purple-100'
                            : 'hover:bg-white/5 text-slate-400'
                        }`}
                      >
                        {sr.type === 'chat' ? (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                          </svg>
                        ) : sr.type === 'voice' ? (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                          </svg>
                        ) : (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                        {sr.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateRoomModal(true)}
                  className="w-full p-4 rounded-2xl border border-dashed border-purple-400/30 bg-purple-500/5 hover:bg-purple-500/10 transition-all group"
                >
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 border border-purple-400/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <svg className="w-4 h-4 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <span className="text-sm text-purple-200">Create Your Room</span>
                  </div>
                </button>
              )}

              {/* Invited Rooms */}
              {invitedRooms.length > 0 && (
                <>
                  <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 px-1 pt-2">Invited Rooms</div>
                  {invitedRooms.map((room) => (
                    <div
                      key={room.id}
                      className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-3 hover:border-emerald-400/40 transition-all cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                          <svg className="w-4 h-4 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-emerald-100">{room.name}</div>
                          <div className="text-[10px] text-emerald-300/60">Invited</div>
                        </div>
                        <svg className="w-4 h-4 text-emerald-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Join Room via Code */}
              <div className="pt-2">
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 px-1 mb-2">Join with Code</div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={roomCodeInput}
                    onChange={(e) => { setRoomCodeInput(e.target.value.toUpperCase()); setRoomCodeError(null); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleJoinRoomByCode()}
                    placeholder="ROOM-CODE"
                    maxLength={10}
                    className="flex-1 px-3 py-2 text-xs bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 font-mono tracking-wider uppercase"
                  />
                  <button
                    onClick={handleJoinRoomByCode}
                    disabled={!roomCodeInput.trim()}
                    className="px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-200 text-xs hover:bg-cyan-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Join
                  </button>
                </div>
                {roomCodeError && (
                  <p className={`text-[10px] mt-1 px-1 ${roomCodeError.includes('access') ? 'text-emerald-400' : 'text-red-400'}`}>
                    {roomCodeError}
                  </p>
                )}
              </div>
            </div>

            {/* Share Code Modal */}
            {showShareCodeModal && generatedRoomCode && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-3xl flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900/95 border border-white/10 rounded-2xl p-5 w-full max-w-xs text-center">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-white">Share Room Code</h3>
                    <button onClick={() => setShowShareCodeModal(false)} className="text-gray-400 hover:text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mb-4">Share this code with friends to invite them to your room</p>

                  {/* Code Display */}
                  <div className="bg-black/40 border border-cyan-400/30 rounded-xl p-4 mb-4">
                    <div className="text-2xl font-mono font-bold text-cyan-300 tracking-[0.2em]">
                      {generatedRoomCode}
                    </div>
                  </div>

                  <button
                    onClick={handleCopyCode}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      copiedCode
                        ? 'bg-emerald-500/20 border border-emerald-400/40 text-emerald-200'
                        : 'bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/30'
                    }`}
                  >
                    {copiedCode ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                        Copy Code
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        );

      case 'music':
        return (
          <>
            <div className="drag-handle flex items-center justify-between mb-3 cursor-move">
              <div className="flex items-center gap-2">
                {/* Music icon - audio bars */}
                <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/10 flex items-end justify-center gap-0.5 pb-1">
                  <div className="w-1 h-2 rounded-full bg-cyan-400/40" />
                  <div className="w-1 h-3 rounded-full bg-cyan-400/60" />
                  <div className="w-1 h-2.5 rounded-full bg-cyan-400/50" />
                  <div className="w-1 h-3.5 rounded-full bg-cyan-400/70" />
                </div>
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-gray-400">Music</div>
              </div>
              <DragHandle />
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <p className="text-xs">No track playing</p>
              </div>
            </div>
          </>
        );

      case 'notifications':
        return (
          <>
            <div className="drag-handle flex items-center justify-between mb-3 cursor-move">
              <div className="flex items-center gap-2">
                {/* Alerts icon - circle with dot */}
                <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center relative">
                  <div className="w-2.5 h-2.5 rounded-full border border-cyan-400/50" />
                  <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-cyan-400/60" />
                </div>
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-gray-400">
                  Alerts
                  {unreadCount > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-[9px] rounded-full bg-cyan-500 text-white">
                      {unreadCount}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllNotificationsAsRead}
                    className="text-[9px] text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <DragHandle />
              </div>
            </div>

            {/* Pending Friend Requests */}
            {pendingRequests.length > 0 && (
              <div className="mb-3">
                <div className="text-[9px] font-mono uppercase tracking-wider text-amber-400/80 mb-2">
                  Friend Requests ({pendingRequests.length})
                </div>
                <div className="space-y-2">
                  {pendingRequests.map((req) => (
                    <div
                      key={req.id}
                      className="p-2 rounded-xl bg-amber-500/10 border border-amber-400/20"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <GlassAvatar
                          src={req.avatar_url}
                          name={req.username}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-amber-100 block truncate">
                            {req.display_name || req.username}
                          </span>
                          <span className="text-[10px] text-slate-400">wants to be friends</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            await acceptFriendRequest(req.id, req.username);
                            loadPendingRequests();
                            loadFriendsList();
                          }}
                          disabled={friendRequestLoading}
                          className="flex-1 px-2 py-1 text-[10px] rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 text-emerald-300 transition-colors disabled:opacity-50"
                        >
                          Accept
                        </button>
                        <button
                          onClick={async () => {
                            await rejectFriendRequest(req.id);
                            loadPendingRequests();
                          }}
                          disabled={friendRequestLoading}
                          className="flex-1 px-2 py-1 text-[10px] rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-300 transition-colors disabled:opacity-50"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Sent Requests (Outgoing) */}
            {pendingSentRequests.length > 0 && (
              <div className="mb-3">
                <div className="text-[9px] font-mono uppercase tracking-wider text-blue-400/80 mb-2">
                  Pending Sent ({pendingSentRequests.length})
                </div>
                <div className="space-y-2">
                  {pendingSentRequests.map((req) => (
                    <div
                      key={req.id}
                      className="p-2 rounded-xl bg-blue-500/10 border border-blue-400/20"
                    >
                      <div className="flex items-center gap-2">
                        <GlassAvatar
                          src={req.avatar_url}
                          name={req.username}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-blue-100 block truncate">
                            {req.display_name || req.username}
                          </span>
                          <span className="text-[10px] text-slate-400">request pending</span>
                        </div>
                        <button
                          onClick={async () => {
                            await cancelFriendRequest(req.id);
                            loadPendingSentRequests();
                          }}
                          disabled={friendRequestLoading}
                          className="px-2 py-1 text-[10px] rounded-lg bg-slate-500/20 hover:bg-red-500/20 border border-slate-400/30 hover:border-red-400/30 text-slate-300 hover:text-red-300 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notifications List */}
            <div className="flex-1 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-cyan-500/20">
              {notificationsLoading ? (
                <div className="text-center text-slate-500 py-8">
                  <p className="text-xs">Loading...</p>
                </div>
              ) : notifications.length > 0 ? (
                notifications.slice(0, 20).map((notif) => (
                  <button
                    key={notif.id}
                    onClick={() => markNotificationAsRead(notif.id)}
                    className={`w-full p-2 rounded-xl text-left transition-all ${
                      notif.read
                        ? 'bg-white/5 border border-white/5'
                        : 'bg-cyan-500/10 border border-cyan-400/20 hover:border-cyan-400/40'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        notif.read ? 'bg-slate-500' : 'bg-cyan-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <span className={`text-xs font-medium block truncate ${
                          notif.read ? 'text-slate-400' : 'text-cyan-100'
                        }`}>
                          {notif.title}
                        </span>
                        {notif.content && (
                          <span className="text-[10px] text-slate-500 block truncate">
                            {notif.content}
                          </span>
                        )}
                        <span className="text-[9px] text-slate-600 mt-1 block">
                          {new Date(notif.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  </button>
                ))
              ) : pendingRequests.length === 0 && pendingSentRequests.length === 0 ? (
                <div className="text-center text-slate-500 py-8">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <p className="text-xs">No notifications yet</p>
                  <p className="text-[10px] text-gray-600 mt-1">You're all caught up!</p>
                </div>
              ) : null}
            </div>
          </>
        );

      case 'media':
        return (
          <>
            <div className="drag-handle flex items-center justify-between mb-3 cursor-move">
              <div className="flex items-center gap-2">
                {/* Media icon - play button in rectangle */}
                <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center relative">
                  <div className="absolute inset-1 rounded-sm border border-fuchsia-400/50" />
                  <div className="w-0 h-0 border-l-[5px] border-l-fuchsia-400/60 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent ml-0.5" />
                </div>
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-gray-400">Media</div>
              </div>
              <div className="flex items-center gap-2">
                {currentMedia && (
                  <button
                    onClick={handleClearMedia}
                    className="w-5 h-5 rounded bg-red-500/20 hover:bg-red-500/40 border border-red-400/30 flex items-center justify-center transition-colors"
                    title="Clear media"
                  >
                    <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <DragHandle />
              </div>
            </div>

            {/* Media Player Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Video/Content Preview */}
              <div className="aspect-video bg-black rounded-xl border border-white/10 mb-3 overflow-hidden relative">
                {currentMedia ? (
                  currentMedia.type === 'youtube' ? (
                    // YouTube Embed
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${currentMedia.videoId}?autoplay=1&rel=0&modestbranding=1`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={currentMedia.title}
                    />
                  ) : (
                    // HTML5 Video/Audio
                    <video
                      ref={videoRef}
                      src={currentMedia.url}
                      className="w-full h-full object-contain"
                      autoPlay
                      controls={false}
                      onTimeUpdate={(e) => {
                        const video = e.currentTarget;
                        setMediaProgress(video.currentTime);
                        setMediaDuration(video.duration || 0);
                      }}
                      onEnded={handleMediaNext}
                      onPlay={() => setIsMediaPlaying(true)}
                      onPause={() => setIsMediaPlaying(false)}
                    />
                  )
                ) : (
                  // Placeholder when no media
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 group">
                    <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-3 group-hover:bg-fuchsia-500/20 group-hover:border-fuchsia-400/50 transition-all">
                      <div className="w-0 h-0 border-l-[16px] border-l-fuchsia-400/60 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent ml-1 group-hover:border-l-fuchsia-400 transition-colors" />
                    </div>
                    <p className="text-xs">No media playing</p>
                    <p className="text-[10px] text-slate-600 mt-1">Paste a YouTube or video URL below</p>
                  </div>
                )}
              </div>

              {/* Now Playing Info */}
              {currentMedia && (
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div className="w-6 h-6 rounded bg-fuchsia-500/20 flex items-center justify-center flex-shrink-0">
                    {currentMedia.type === 'youtube' ? (
                      <svg className="w-3 h-3 text-red-400" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    ) : (
                      <div className="w-0 h-0 border-l-[6px] border-l-fuchsia-400 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent ml-0.5" />
                    )}
                  </div>
                  <span className="text-xs text-fuchsia-200 truncate flex-1">{currentMedia.title}</span>
                  {currentMedia.type !== 'youtube' && mediaDuration > 0 && (
                    <span className="text-[10px] text-slate-500">
                      {formatMediaTime(mediaProgress)} / {formatMediaTime(mediaDuration)}
                    </span>
                  )}
                </div>
              )}

              {/* Media Controls */}
              <div className="flex items-center gap-2 mb-3 px-1">
                {/* Previous */}
                <button
                  onClick={handleMediaPrev}
                  disabled={!currentMedia}
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-fuchsia-500/20 hover:border-fuchsia-400/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-0.5">
                    <div className="w-0.5 h-3 bg-current opacity-60" />
                    <div className="w-0 h-0 border-r-[6px] border-r-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent opacity-60" />
                  </div>
                </button>

                {/* Play/Pause */}
                <button
                  onClick={handleMediaPlayPause}
                  disabled={!currentMedia || currentMedia.type === 'youtube'}
                  className="w-10 h-10 rounded-xl bg-fuchsia-500/20 border border-fuchsia-400/30 flex items-center justify-center hover:bg-fuchsia-500/30 transition-all disabled:opacity-50"
                  title={currentMedia?.type === 'youtube' ? 'Use YouTube controls' : (isMediaPlaying ? 'Pause' : 'Play')}
                >
                  {isMediaPlaying && currentMedia?.type !== 'youtube' ? (
                    // Pause icon
                    <div className="flex gap-1">
                      <div className="w-1 h-4 bg-fuchsia-300 rounded-sm" />
                      <div className="w-1 h-4 bg-fuchsia-300 rounded-sm" />
                    </div>
                  ) : (
                    // Play icon
                    <div className="w-0 h-0 border-l-[10px] border-l-fuchsia-300 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent ml-0.5" />
                  )}
                </button>

                {/* Next */}
                <button
                  onClick={handleMediaNext}
                  disabled={mediaQueue.length === 0}
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-fuchsia-500/20 hover:border-fuchsia-400/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-0.5">
                    <div className="w-0 h-0 border-l-[6px] border-l-current border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent opacity-60" />
                    <div className="w-0.5 h-3 bg-current opacity-60" />
                  </div>
                </button>

                {/* Spacer */}
                <div className="flex-1" />

                {/* Queue count */}
                {mediaQueue.length > 0 && (
                  <span className="text-[10px] text-slate-500 px-2">{mediaQueue.length} in queue</span>
                )}
              </div>

              {/* Progress Bar (for non-YouTube videos) */}
              {currentMedia && currentMedia.type !== 'youtube' && (
                <div
                  className="h-1.5 bg-white/5 rounded-full mb-3 mx-1 overflow-hidden cursor-pointer group"
                  onClick={(e) => {
                    if (videoRef.current && mediaDuration > 0) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percent = (e.clientX - rect.left) / rect.width;
                      videoRef.current.currentTime = percent * mediaDuration;
                    }
                  }}
                >
                  <div
                    className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-full transition-all group-hover:from-fuchsia-400 group-hover:to-pink-400"
                    style={{ width: mediaDuration > 0 ? `${(mediaProgress / mediaDuration) * 100}%` : '0%' }}
                  />
                </div>
              )}

              {/* Queue Section */}
              <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-fuchsia-500/20">
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 px-1 mb-2">
                  Queue {mediaQueue.length > 0 && `(${mediaQueue.length})`}
                </div>
                {mediaQueue.length > 0 ? (
                  <div className="space-y-1">
                    {mediaQueue.map((item, index) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10 group"
                      >
                        <span className="text-[10px] text-slate-500 w-4">{index + 1}</span>
                        <div className="w-5 h-5 rounded bg-fuchsia-500/20 flex items-center justify-center flex-shrink-0">
                          {item.type === 'youtube' ? (
                            <svg className="w-2.5 h-2.5 text-red-400" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                            </svg>
                          ) : (
                            <div className="w-0 h-0 border-l-[4px] border-l-fuchsia-400 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent ml-0.5" />
                          )}
                        </div>
                        <span className="text-[11px] text-slate-300 truncate flex-1">{item.title}</span>
                        <button
                          onClick={() => handleRemoveFromQueue(item.id)}
                          className="w-5 h-5 rounded bg-red-500/0 hover:bg-red-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-slate-500 py-4">
                    <p className="text-xs">Queue is empty</p>
                    <p className="text-[10px] text-slate-600 mt-1">Add more videos to play next</p>
                  </div>
                )}
              </div>

              {/* Search/URL Mode Toggle */}
              <div className="mt-2 flex items-center gap-2 mb-2">
                <button
                  onClick={() => { setIsSearchMode(false); setSearchResults([]); }}
                  className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                    !isSearchMode
                      ? 'bg-fuchsia-500/20 border border-fuchsia-400/40 text-fuchsia-200'
                      : 'bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  Paste URL
                </button>
                <button
                  onClick={() => { setIsSearchMode(true); setMediaError(null); }}
                  className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all flex items-center justify-center gap-1 ${
                    isSearchMode
                      ? 'bg-fuchsia-500/20 border border-fuchsia-400/40 text-fuchsia-200'
                      : 'bg-white/5 border border-white/10 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search YouTube
                </button>
              </div>

              {/* URL Input Mode */}
              {!isSearchMode && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={mediaUrlInput}
                    onChange={(e) => { setMediaUrlInput(e.target.value); setMediaError(null); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddMedia()}
                    placeholder="Paste YouTube or video URL..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs placeholder-slate-500 focus:outline-none focus:border-fuchsia-400/50"
                  />
                  <button
                    onClick={handleAddMedia}
                    disabled={!mediaUrlInput.trim()}
                    className="px-3 py-2 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/30 text-fuchsia-200 text-xs hover:bg-fuchsia-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {currentMedia ? 'Queue' : 'Play'}
                  </button>
                </div>
              )}

              {/* Search Mode */}
              {isSearchMode && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchYouTube(searchQuery)}
                      placeholder="Search for videos..."
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs placeholder-slate-500 focus:outline-none focus:border-fuchsia-400/50"
                    />
                    <button
                      onClick={() => searchYouTube(searchQuery)}
                      disabled={!searchQuery.trim() || isSearching}
                      className="px-3 py-2 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/30 text-fuchsia-200 text-xs hover:bg-fuchsia-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSearching ? (
                        <div className="w-4 h-4 border-2 border-fuchsia-400/30 border-t-fuchsia-400 rounded-full animate-spin" />
                      ) : (
                        'Search'
                      )}
                    </button>
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border border-white/10 bg-black/30 p-2">
                      {searchResults.map((result) => (
                        <button
                          key={result.videoId}
                          onClick={() => handleSelectSearchResult(result)}
                          className="w-full flex items-center gap-2 p-1.5 rounded-lg hover:bg-fuchsia-500/10 transition-all group text-left"
                        >
                          <img
                            src={result.thumbnail}
                            alt=""
                            className="w-12 h-9 rounded object-cover flex-shrink-0"
                            onError={(e) => { (e.target as HTMLImageElement).src = `https://i.ytimg.com/vi/${result.videoId}/mqdefault.jpg`; }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] text-slate-200 truncate group-hover:text-fuchsia-200 transition-colors">
                              {result.title}
                            </p>
                            <p className="text-[10px] text-slate-500 truncate">
                              {result.author} • {formatVideoDuration(result.lengthSeconds)}
                            </p>
                          </div>
                          <div className="w-5 h-5 rounded bg-fuchsia-500/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="w-0 h-0 border-l-[5px] border-l-fuchsia-400 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent ml-0.5" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Error Display */}
              {mediaError && (
                <div className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-400/20">
                  <p className="text-[10px] text-red-300">{mediaError}</p>
                </div>
              )}
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`relative w-full h-screen bg-[#05070b] overflow-hidden font-sans text-slate-100 ${className}`}>
      {/* Header */}
      <div className="fixed left-6 top-6 z-[100] flex items-center">
        {/* GJYL Logo as the "G" - Click to switch back to Standard mode */}
        <button
          onClick={() => onSwitchStyle?.('standard')}
          className="group relative flex-shrink-0 -mt-6"
          title="Switch to Standard Mode"
        >
          <img
            src="/logos/gjyl-logo.png"
            alt="G"
            className="w-10 h-10 rounded-xl object-contain transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_25px_rgba(34,211,238,0.6)]"
          />
          <div className="absolute inset-0 rounded-xl bg-cyan-400/0 group-hover:bg-cyan-400/10 transition-colors" />
        </button>

        {/* JYL PUBLIC HUB - positioned tight to logo */}
        <div className="-ml-1">
          <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-100 via-cyan-400 to-purple-400 drop-shadow-[0_0_12px_rgba(34,211,238,0.75)]">
            JYL PUBLIC HUB
          </h1>
          <span className="text-[9px] font-mono text-cyan-500/50 tracking-[0.2em]">
            JARVIS MODE
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="fixed right-6 top-6 z-[100] flex items-center gap-2">
        <button
          onClick={() => setShowPlanetModal(true)}
          className="px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/80 to-cyan-500/80 text-white text-[11px] font-semibold uppercase shadow-[0_0_20px_rgba(139,92,246,0.5)] hover:-translate-y-0.5 transition-all flex items-center gap-2"
        >
          {/* Custom orbit icon */}
          <div className="w-4 h-4 relative">
            <div className="w-2 h-2 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80" />
            <div className="absolute inset-0 border border-white/40 rounded-full" />
            <div className="absolute w-1 h-1 rounded-full bg-white top-0 right-0.5" />
          </div>
          <span>Islands ({visibleIslands.length})</span>
        </button>

        <button
          onClick={snapIslands}
          disabled={isLayoutLocked}
          className={`px-3 py-2 rounded-full text-[11px] font-semibold uppercase border transition-all flex items-center gap-1.5 ${
            isLayoutLocked
              ? 'bg-white/5 border-white/5 text-slate-500 cursor-not-allowed'
              : 'bg-white/5 text-cyan-100 border-white/10 hover:border-cyan-400/50'
          }`}
        >
          {/* Custom snap icon - converging arrows */}
          <div className="w-3 h-3 relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0.5 h-1.5 bg-current rounded-full" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0.5 h-1.5 bg-current rounded-full" />
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-0.5 bg-current rounded-full" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-0.5 bg-current rounded-full" />
          </div>
          Snap
        </button>

        {/* Global layout lock - custom glassmorphism style */}
        <button
          onClick={() => setIsLayoutLocked(!isLayoutLocked)}
          className={`px-3 py-2 rounded-full text-[11px] font-semibold uppercase border transition-all flex items-center gap-2 ${
            isLayoutLocked
              ? 'bg-cyan-500/10 border-cyan-400/40 text-cyan-200'
              : 'bg-white/5 border-white/10 text-slate-400 hover:text-cyan-100'
          }`}
        >
          {/* Custom lock indicator - two bars */}
          <div className="flex flex-col gap-0.5">
            <div className={`w-3 h-0.5 rounded-full transition-all ${isLayoutLocked ? 'bg-cyan-400' : 'bg-slate-500'}`} />
            <div className={`w-3 h-0.5 rounded-full transition-all ${isLayoutLocked ? 'bg-cyan-400' : 'bg-slate-500'}`} />
          </div>
          <span>{isLayoutLocked ? 'Locked' : 'Lock'}</span>
        </button>
      </div>

      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-80"
          style={{
            backgroundImage: "url('https://cdn.pixabay.com/photo/2017/02/07/10/57/moon-2048727_1280.jpg')",
            filter: `brightness(${0.75 + bgIntensity * 0.25}) saturate(${0.9 + bgIntensity * 0.2})`,
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)`,
            backgroundSize: '38px 38px',
            opacity: 0.14 * bgIntensity,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/35 to-transparent" />
        {backgroundMotion && <div className="absolute inset-0 bg-cyan-500/10 animate-pulse" />}
      </div>

      {/* Dynamic Islands */}
      {visibleIslands.map((island) => {
        const config = ISLAND_REGISTRY[island.type];

        // During snapping, use pure CSS positioning for smooth animation
        if (isSnapping) {
          return (
            <div
              key={island.type}
              className="absolute"
              style={{
                left: island.position.x,
                top: island.position.y,
                width: island.position.width,
                height: island.position.height,
                zIndex: activeIsland === island.type ? 50 : 10,
                transition: transitionStyle,
              }}
            >
              <div
                className={`${dynamicGlassPanel} w-full h-full p-4 flex flex-col`}
                style={{
                  background: `linear-gradient(to bottom, rgba(31,41,55,${0.32 * panelOpacity}), rgba(0,0,0,${0.85 * panelOpacity}))`,
                  boxShadow: dynamicBevelShadow,
                }}
              >
                {renderIslandContent(island.type)}
              </div>
            </div>
          );
        }

        // Normal draggable mode
        const isLocked = island.locked;
        const isOfficial = OFFICIAL_ISLANDS.includes(island.type);
        return (
          <Rnd
            key={island.type}
            position={{ x: island.position.x, y: island.position.y }}
            size={{ width: island.position.width, height: island.position.height }}
            onDragStop={(e, d) => updateIslandPosition(island.type, d.x, d.y)}
            onResizeStop={(e, dir, ref, delta, pos) => {
              updateIslandSize(island.type, parseInt(ref.style.width), parseInt(ref.style.height), pos.x, pos.y);
            }}
            minWidth={config.minWidth}
            minHeight={config.minHeight}
            bounds="parent"
            disableDragging={isLayoutLocked || isLocked}
            enableResizing={!isLayoutLocked && !isLocked}
            onMouseDown={() => setActiveIsland(island.type)}
            style={{ zIndex: activeIsland === island.type ? 50 : 10 }}
            dragHandleClassName="drag-handle"
          >
            <div
              className={`${dynamicGlassPanel} w-full h-full p-4 flex flex-col ${isLocked ? 'ring-1 ring-cyan-500/30' : ''}`}
              style={{
                background: `linear-gradient(to bottom, rgba(31,41,55,${0.32 * panelOpacity}), rgba(0,0,0,${0.85 * panelOpacity}))`,
                boxShadow: dynamicBevelShadow,
              }}
            >
              {renderIslandContent(island.type)}

              {/* Official GJYL indicator - three small diamonds */}
              {isOfficial && (
                <div className="absolute top-3 left-3 flex items-center gap-1" title="Official GJYL">
                  <div className="w-1.5 h-1.5 rotate-45 bg-cyan-400/60" />
                  <div className="w-1.5 h-1.5 rotate-45 bg-cyan-400/40" />
                  <div className="w-1.5 h-1.5 rotate-45 bg-cyan-400/20" />
                </div>
              )}

              {/* Pin/Anchor button - glassmorphism style */}
              {!isLayoutLocked && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleIslandLock(island.type); }}
                  className={`absolute top-3 right-3 w-6 h-6 rounded-lg flex items-center justify-center transition-all duration-300 ${
                    isLocked
                      ? 'bg-cyan-500/20 border border-cyan-400/50 shadow-[0_0_12px_rgba(34,211,238,0.4)]'
                      : 'bg-white/5 border border-white/10 hover:border-white/30 opacity-40 hover:opacity-100'
                  }`}
                  title={isLocked ? 'Unpin island' : 'Pin island in place'}
                >
                  {/* Custom pin/diamond shape */}
                  <div className={`w-2 h-2 rotate-45 transition-all ${
                    isLocked
                      ? 'bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]'
                      : 'bg-white/40'
                  }`} />
                </button>
              )}

              {/* Resize handle */}
              {!isLayoutLocked && !isLocked && (
                <div className="absolute bottom-2 right-2 text-cyan-500/30">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
                  </svg>
                </div>
              )}
            </div>
          </Rnd>
        );
      })}

      {/* Planet Modal */}
      <PlanetModal
        isOpen={showPlanetModal}
        onClose={() => setShowPlanetModal(false)}
        islands={islands}
        onToggleIsland={toggleIsland}
        onSnapIslands={() => { snapIslands(); setShowPlanetModal(false); }}
      />

      {/* Profile Modal - view own profile */}
      <ProfileModal
        isOpen={showProfileModal && viewingProfile?.id === currentUser?.id}
        onClose={() => { setShowProfileModal(false); setViewingProfile(null); }}
        profile={myProfile}
        isOwnProfile={true}
        onUpdateProfile={updateProfile}
        onUploadAvatar={uploadAvatar}
      />

      {/* Profile Modal - view other user's profile */}
      <ProfileModal
        isOpen={showProfileModal && viewingProfile?.id !== currentUser?.id}
        onClose={() => { setShowProfileModal(false); setViewingProfile(null); }}
        profile={viewingProfile}
        isOwnProfile={false}
      />

      {/* Incoming Call Overlay */}
      {incomingCall && callState === 'ringing' && (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-md flex items-center justify-center">
          <div className="bg-gradient-to-b from-gray-800/90 to-gray-900/95 border border-white/10 rounded-3xl p-8 w-full max-w-sm text-center shadow-[0_0_100px_rgba(34,211,238,0.2)]">
            {/* Caller Avatar */}
            <div className="relative w-24 h-24 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 animate-pulse" />
              <div className="absolute inset-1 rounded-full bg-gray-900 flex items-center justify-center">
                {incomingCall.callerAvatar ? (
                  <img src={incomingCall.callerAvatar} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-cyan-400">{incomingCall.callerName?.[0]?.toUpperCase() || '?'}</span>
                )}
              </div>
              {/* Ring animation */}
              <div className="absolute -inset-2 rounded-full border-2 border-cyan-400/50 animate-ping" />
            </div>

            {/* Caller Info */}
            <h3 className="text-xl font-bold text-white mb-1">{incomingCall.callerName}</h3>
            <p className="text-sm text-cyan-400 mb-6">
              {incomingCall.type === 'video' ? 'Video Call' : 'Voice Call'}
            </p>

            {/* Action Buttons */}
            <div className="flex items-center justify-center gap-6">
              {/* Reject */}
              <button
                onClick={rejectCall}
                className="w-16 h-16 rounded-full bg-red-500/20 hover:bg-red-500/40 border-2 border-red-500 flex items-center justify-center transition-all group"
              >
                <svg className="w-8 h-8 text-red-500 group-hover:scale-110 transition-transform rotate-135" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </button>

              {/* Accept */}
              <button
                onClick={acceptCall}
                className="w-16 h-16 rounded-full bg-emerald-500/20 hover:bg-emerald-500/40 border-2 border-emerald-500 flex items-center justify-center transition-all group animate-bounce"
              >
                <svg className="w-8 h-8 text-emerald-500 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Call Overlay */}
      {activeCall && (callState === 'calling' || callState === 'connected') && (
        <div className="fixed bottom-6 right-6 z-[200] w-80">
          <div className="bg-gradient-to-b from-gray-800/95 to-gray-900/98 border border-white/10 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(34,211,238,0.2)]">
            {/* Video Preview (if video call) */}
            {isVideoOn && remoteStream && (
              <div className="relative aspect-video bg-black">
                <video
                  autoPlay
                  playsInline
                  ref={(video) => { if (video && remoteStream) video.srcObject = remoteStream; }}
                  className="w-full h-full object-cover"
                />
                {/* Local video PIP */}
                {localStream && (
                  <div className="absolute bottom-2 right-2 w-20 h-14 rounded-lg overflow-hidden border border-white/20">
                    <video
                      autoPlay
                      playsInline
                      muted
                      ref={(video) => { if (video && localStream) video.srcObject = localStream; }}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Call Info */}
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500/30 to-purple-500/30 flex items-center justify-center border border-cyan-400/30">
                  {(activeCall.callerId === currentUser?.id ? activeCall.receiverAvatar : activeCall.callerAvatar) ? (
                    <img
                      src={activeCall.callerId === currentUser?.id ? activeCall.receiverAvatar! : activeCall.callerAvatar!}
                      alt=""
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-bold text-cyan-400">
                      {(activeCall.callerId === currentUser?.id ? activeCall.receiverName : activeCall.callerName)?.[0]?.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-white truncate">
                    {activeCall.callerId === currentUser?.id ? activeCall.receiverName : activeCall.callerName}
                  </h4>
                  <p className="text-xs text-cyan-400">
                    {callState === 'calling' ? 'Calling...' : formatDuration(callDuration)}
                  </p>
                </div>
                {/* Call type indicator */}
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                  {activeCall.type === 'video' ? (
                    <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Call Controls */}
              <div className="flex items-center justify-center gap-3">
                {/* Mute */}
                <button
                  onClick={toggleCallMute}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    callIsMuted
                      ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                      : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                  }`}
                  title={callIsMuted ? 'Unmute' : 'Mute'}
                >
                  {callIsMuted ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  )}
                </button>

                {/* Deafen */}
                <button
                  onClick={toggleCallDeafen}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    callIsDeafened
                      ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                      : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                  }`}
                  title={callIsDeafened ? 'Undeafen' : 'Deafen'}
                >
                  {callIsDeafened ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                  )}
                </button>

                {/* Video Toggle (if video call) */}
                {activeCall.type === 'video' && (
                  <button
                    onClick={toggleVideo}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      !isVideoOn
                        ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                        : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                    }`}
                    title={isVideoOn ? 'Turn off video' : 'Turn on video'}
                  >
                    {isVideoOn ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                      </svg>
                    )}
                  </button>
                )}

                {/* End Call */}
                <button
                  onClick={endCall}
                  className="w-12 h-10 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all"
                  title="End call"
                >
                  <svg className="w-5 h-5 text-white rotate-135" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </button>
              </div>

              {/* Error Message */}
              {callError && (
                <p className="mt-2 text-xs text-red-400 text-center">{callError}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Context Menu (right-click on users) */}
      {contextMenu.user && (
        <UserContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          onClose={closeContextMenu}
          userId={contextMenu.user.id}
          username={contextMenu.user.username}
          displayName={contextMenu.user.display_name || undefined}
          avatarUrl={contextMenu.user.avatar_url}
          isFriend={contextMenu.user.isFriend}
          isPendingSent={contextMenu.user.isPendingSent}
          isPendingReceived={contextMenu.user.isPendingReceived}
          isCurrentUser={contextMenu.user.id === currentUser?.id}
          onViewProfile={async () => {
            const profile = await getProfile(contextMenu.user!.id);
            if (profile) {
              setViewingProfile(profile);
              setShowProfileModal(true);
            }
          }}
          onSendMessage={async () => {
            if (!contextMenu.user) return;
            const conversationId = await getOrCreateConversation(contextMenu.user.id);
            if (conversationId) {
              const conv = dmConversations.find(c => c.id === conversationId);
              if (conv) {
                setActiveDMConversation(conv);
                setShowDMChat(true);
              }
            }
          }}
          onVoiceCall={() => {
            if (!contextMenu.user) return;
            handleStartCall(
              contextMenu.user.id,
              contextMenu.user.display_name || contextMenu.user.username,
              contextMenu.user.avatar_url || null,
              'voice'
            );
          }}
          onVideoCall={() => {
            if (!contextMenu.user) return;
            handleStartCall(
              contextMenu.user.id,
              contextMenu.user.display_name || contextMenu.user.username,
              contextMenu.user.avatar_url || null,
              'video'
            );
          }}
          onAddFriend={async () => {
            if (!contextMenu.user) return;
            await sendFriendRequest(contextMenu.user.id, contextMenu.user.username);
          }}
          onRemoveFriend={async () => {
            if (!contextMenu.user) return;
            await removeFriend(contextMenu.user.id);
          }}
          onAcceptFriend={async () => {
            if (!contextMenu.user) return;
            // Find the pending request for this user
            const request = pendingRequests.find(r => r.user_id === contextMenu.user!.id);
            if (request) {
              await acceptFriendRequest(request.id, request.username);
            }
          }}
          onBlockUser={async () => {
            if (!contextMenu.user) return;
            await blockUser(contextMenu.user.id);
          }}
          onUnblockUser={async () => {
            if (!contextMenu.user) return;
            await unblockUser(contextMenu.user.id);
          }}
          onCopyUserId={() => {
            // Clipboard copy is handled in the component
          }}
        />
      )}
    </div>
  );
}
